import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import Pagination from "./Pagination";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import Video from "twilio-video";
import MobileBottomNav from "./MobileBottomNav";
import "./styles/Myprofile.css";
import "../styles/global.css";
import { flyToCart } from "../utils/flyToCart";

/* ─── ALL ORIGINAL CONSTANTS (unchanged) ─── */
const ORDER_STEPS = [
  { label: "Confirmed", icon: "fa-solid fa-check" },
  { label: "Processing", icon: "fa-solid fa-gear" },
  { label: "Quality Check", icon: "fa-solid fa-shield" },
  { label: "Dispatched", icon: "fa-solid fa-truck-fast" },
  { label: "Delivered", icon: "fa-solid fa-house" },
];

const ORDER_STEP_COLORS = [
  { bg: "#e6f4f6", color: "#1E7A8C", activeBg: "#1E7A8C", border: "#a8d8df" },
  { bg: "#fff8e1", color: "#f59e0b", activeBg: "#f59e0b", border: "#fcd34d" },
  { bg: "#f3e8ff", color: "#9333ea", activeBg: "#9333ea", border: "#d8b4fe" },
  { bg: "#e0f2fe", color: "#0284c7", activeBg: "#0284c7", border: "#7dd3fc" },
  { bg: "#dcfce7", color: "#16a34a", activeBg: "#16a34a", border: "#86efac" },
];

const STATUS_STEP_MAP = {
  "Confirmed Order": 0,
  "Processing Order": 1,
  "Quality Check": 2,
  "Product Dispatched": 3,
  "Product Delivered": 4,
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Delivered": case "Product Delivered": return "badge-delivered";
    case "Processing": case "Processing Order": return "badge-processing";
    case "Cancelled": return "badge-cancelled";
    case "Product Dispatched": case "Dispatched": return "badge-dispatched";
    case "Paid": return "badge-delivered";
    case "Un Paid": return "badge-cancelled";
    default: return "badge-pending";
  }
};

/* ═══════════════════════════════════════════════════════════════
   APPOINTMENT TYPE HELPERS
═══════════════════════════════════════════════════════════════ */
const APPT_TYPE = { ONLINE: "online", IN_PATIENT: "in_patient", UNKNOWN: "unknown" };

const normalizeApptType = (raw) => {
  if (raw === null || raw === undefined || raw === "") return APPT_TYPE.UNKNOWN;
  const t = String(raw).toLowerCase().replace(/[\s_\-]/g, "");
  if (["online", "onlinevideo", "video", "virtual", "tele", "telemedicine"].includes(t)) return APPT_TYPE.ONLINE;
  if (["inpatient", "inperson", "offline", "hospital", "clinic", "physical"].includes(t)) return APPT_TYPE.IN_PATIENT;
  return APPT_TYPE.UNKNOWN;
};

const getApptType = (appt) => normalizeApptType(
  appt?.appointment_type ?? appt?.appointmentType ?? appt?.booking_type ?? appt?.bookingType
);

const getAppointmentTypeLabel = (type) => {
  const t = normalizeApptType(type);
  if (t === APPT_TYPE.ONLINE)     return { label: "Online Video", cls: "appt-type-online",    icon: "fa-solid fa-video" };
  if (t === APPT_TYPE.IN_PATIENT) return { label: "In Patient",   cls: "appt-type-inpatient", icon: "fa-solid fa-hospital-user" };
  return { label: "Not Specified", cls: "appt-type-none", icon: "fa-solid fa-circle-question" };
};

const isBeforeAppointmentTime = (date, time) => {
  if (!date || !time) return true;
  const dt = new Date(`${date} ${time}`);
  if (isNaN(dt.getTime())) return true;
  return new Date() < dt;
};

const isVideoAllowed = (appt, durationMin = 30) => {
  if (getApptType(appt) !== APPT_TYPE.ONLINE) return false;
  if (!appt?.booking_date || !appt?.booking_time) return false;
  const start = new Date(`${appt.booking_date} ${appt.booking_time}`);
  if (isNaN(start.getTime())) return false;
  const now = new Date();
  const end = new Date(start.getTime() + durationMin * 60000);
  return now >= start && now <= end;
};

const formatApptDateTime = (date, time) => {
  if (!date) return "TBD";
  try {
    const d = new Date(`${date} ${time || "00:00"}`);
    if (isNaN(d.getTime())) return `${date} ${time || ""}`.trim();
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return `${date} ${time || ""}`.trim();
  }
};

const sanitizeChatText = (s) => (typeof s === "string" ? s.slice(0, 1000) : "");

const forceCloseModal = (id) => {
  try {
    const el = document.getElementById(id);
    if (!el) return;
    const inst = window.bootstrap?.Modal?.getInstance(el);
    if (inst) inst.hide();
    setTimeout(() => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.querySelectorAll(".modal-backdrop").forEach(b => b.remove());
      el.classList.remove("show");
      el.setAttribute("aria-hidden", "true");
      el.style.display = "";
    }, 300);
  } catch (e) { console.error("forceCloseModal:", e); }
};

/* ═══════════════════════════════════════════════════════════════
   HEALTH ASSESSMENT — OUTSIDE Checkout component
═══════════════════════════════════════════════════════════════ */
const buildEmptyAssessment = () => ({
  regNo: "", branch: "", date: "",
  patientName: "", dob: "", age: "", sex: "",
  address1: "", address2: "", address3: "", address4: "",
  contact: "", email: "",
  chiefComplaints: "", chiefDuration: "",
  digestive: { Indigestion: false, Acidity: false, Ulcer: false, IBS: false, Gastritis: false, GERD: false, _duration: "" },
  hematological: { Anemia: false, Hypertension: false, "Varicose vein": false, Cholesterol: false, Triglyceride: false, _duration: "" },
  muscular: { Obesity: false, "Weight Loss": false, "Muscle pain": false, Debility: false, _duration: "" },
  skin: { Itching: false, Discolouration: false, Vitiligo: false, Eczema: false, Psoriasis: false, _duration: "" },
  skeletal: { "Osteo Arthritis": false, "Rheumatoid arthritis": false, "Disc prolapse": false, "Lumbar Spondylosis": false, "Cervical Spondylosis": false, Sciatica: false, Gout: false, _duration: "" },
  nervous: { "Parkinson's disease": false, Paralysis: false, "Alzheimer's disease": false, Immunity: false, _duration: "" },
  anorectal: { Piles: false, Fistula: false, "Fissure in ano": false, _duration: "" },
  reproductive: { "Male Infertility": false, "Prostate Disorders": false, "Erectile dysfunction": false, "Female Infertility": false, Leucorrhoea: false, Amenorrhoea: false, Dysmenorrhoea: false, "Irregular menses": false, PCOD: false, Fibroid: false, Endometriosis: false, "Monopausal Syndrome": false, _duration: "" },
  kidney: { "Kidney stone": false, "Kidney failure": false, "Diabetes Mellitus": false, "Diabetes neuritis": false, "Diabetes nephritis": false, "Diabetic foot ulcer": false, _duration: "" },
  cardiac: { "Angina Pectoris": false, "Coronary Artery diseases": false, Cardiomyopathy: false, Atherosclerosis: false, "Fatty liver": false, "Liver Cirrhosis": false, _duration: "" },
  neckThoracic: { Thyroid: false, Asthma: false, Wheezing: false, _duration: "" },
  headEnt: { ENT: false, Sinusitis: false, Migraine: false, Anxiety: false, Stress: false, ADHD: false, "Hair Loss": false, _duration: "" },
  rehabCancer: "", rehabAsadhya: "",
  appetite: "", bowel: { Regular: false, Constipated: false, _other: "" },
  micturation: { Normal: false, "Increased frequency": false, "Decreased freqency": false, "Burning sensation": false, Incontenence: false },
  sleep: { Sound: false, Disturbed: false, _other: "" },
  pain: "",
  stress: { Yes: false, No: false, Personal: false, Professional: false },
  lmp: "", menstrualCycle: "", menstrualFlow: "",
  dysmenorrhoea: "", leucorrhoea: "", delivery: "",
  specificComplaints: "",
  pastHypertension: "", pastDiabetes: "", pastPiles: "", pastThyroid: "",
  pastDuration: "",
  otherDiseases: "", otherDuration: "",
  familyHistory: "",
  surgicalHistory: "",
  medicalHistory: "",
  dietHistory: "", waterIntake: "", addictions: "",
  physicalActivity: "", physicalActivityDuration: "",
  height: "", bp: "", weight: "", pulse: "", skin2: "", vision: "",
  physicalExam: "", labInvestigation: "", diagnosis: "",
  dosha: { Vata: false, Pitha: false, Kapha: false, Vatapitha: false, Vatakapha: false, Pithakapha: false, Vatapithakapha: false },
  dhatu: { Rasa: false, Raktha: false, Mamsa: false, Medas: false, Asthi: false, Majja: false, Shukra: false },
  chakra: { Mooladhara: false, Swadhisthana: false, Manipura: false, Anahata: false, Vishuddhi: false, Ajna: false, Sahasrara: false },
  marma: ["", "", "", "", "", "", "", "", ""],
  kosha: { Annamaya: false, Pranamaya: false, Manomaya: false, Vigyanamaya: false, Anandamaya: false },
  doctorChief: "", associateDoctor: "",
});

const loadAssessment = async (appointmentId) => {
  const res = await fetch(API_BASE_URL + "api/get-health-assessment/" + appointmentId);
  const data = await res.json();
  return data.data;
};

/* ═══════════════════════════════════════════════════════════════
   HEALTH ASSESSMENT FORM COMPONENT — view-only for patients
═══════════════════════════════════════════════════════════════ */
const HealthAssessmentForm = ({ open, mode, appointment, onClose }) => {
  const [form, setForm] = useState(buildEmptyAssessment());

  useEffect(() => {
    if (!open || !appointment) return;
    const fetchData = async () => {
      try {
        const existing = await loadAssessment(appointment.id);
        if (existing && Object.keys(existing).length > 0) {
          setForm({ ...buildEmptyAssessment(), ...existing });
        } else {
          setForm({
            ...buildEmptyAssessment(),
            patientName: appointment.patient_name || "",
            contact: appointment.mobile || "",
            date: appointment.booking_date || "",
            regNo: `#${appointment.id}`,
          });
        }
      } catch (err) { console.error("Load error:", err); }
    };
    fetchData();
  }, [open, appointment]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !appointment) return null;

  // Patients can only VIEW — all inputs are readOnly
  const Cb = ({ group, name }) => (
    <label className="hba-cb">
      <input type="checkbox" checked={!!form[group]?.[name]} disabled readOnly />
      <span>{name}</span>
    </label>
  );

  const DisorderRow = ({ title, sub, group, items, even }) => (
    <tr>
      <td className="hba-cell-label" data-even={even}>
        <span className="hba-d-title">{title}</span>
        <span className="hba-d-sub">({sub})</span>
      </td>
      <td className="hba-cell-items" data-even={even}>
        <div className="hba-cb-grid">
          {items.map((it) => <Cb key={it} group={group} name={it} />)}
        </div>
      </td>
      <td className="hba-cell-duration">
        <span className="hba-d-dur-lbl">Duration:</span>
        <input type="text" className="hba-input hba-input-dur" value={form[group]?._duration || ""} readOnly />
      </td>
    </tr>
  );

  const handleDownloadPdf = () => {
    const printContents = document.getElementById("hba-printable");
    if (!printContents) return;
    const printWindow = window.open("", "", "height=900,width=1200");
    printWindow.document.write(`<html><head><title>Health Assessment Form</title>
      <style>
        body { font-family: Georgia, serif; color: #222; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        td { border: 1px solid #e0cfa0; padding: 8px; font-size: 13px; }
        input[type="text"], textarea { width: 100%; border: none; font-family: inherit; font-size: 13px; }
        input[type="checkbox"] { margin-right: 4px; }
        .hba-logo-text { font-size: 22px; font-weight: 700; color: #b8860b; text-align: center; padding: 10px 0; }
        .hba-section-h2 { color: #aa804c; font-size: 20px; }
        .hba-section-title { font-weight: bold; font-size: 14px; color: #b8860b; border-bottom: 2px solid #b8860b; padding-bottom: 4px; margin: 14px 0; display: inline-block; }
        .hba-meta-row { background: #f5ede0; padding: 10px; display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; border-radius: 4px; margin: 10px 0; }
        .hba-page { margin-bottom: 20px; padding: 20px; border: 1px solid #cea465; page-break-after: always; }
        .hba-cb-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 4px; }
        .hba-ayur-row { display: grid; grid-template-columns: 22% 1fr; padding: 10px; border: 1px solid #e8d9b8; margin-bottom: 6px; }
        @media print { .hba-page { page-break-after: always; } }
      </style>
    </head><body>${printContents.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); printWindow.close(); }, 500);
  };

  return (
    <div className="hba-overlay" role="dialog" aria-modal="true" aria-labelledby="hba-title">
      {/* Toolbar */}
      <div className="hba-toolbar">
        <div className="hba-toolbar-left">
          <h3 id="hba-title" className="hba-toolbar-title">
            <i className="fa-solid fa-clipboard-user"></i>
            Health Assessment Form
          </h3>
          <span className="hba-toolbar-sub">
            #{appointment.id} · {appointment.patient_name}
          </span>
        </div>
        <div className="hba-toolbar-actions">
          <button className="hba-btn hba-btn--ghost" onClick={handleDownloadPdf} title="Download PDF / Print">
            <i className="fa-solid fa-file-pdf"></i> PDF
          </button>
          <button className="hba-btn hba-btn--close" onClick={onClose} title="Close">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      {/* Scrollable form body */}
      <div className="hba-scroll">
        <div className="hba-page-wrap" id="hba-printable">

          {/* ─────── PAGE 1 ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-logo-strip">
              <div className="hba-logo-text">Dr. Shreevarma's Wellness</div>
            </div>
            <div className="hba-row-between">
              <h2 className="hba-section-h2">Health Assessment Form</h2>
            </div>
            <div className="hba-meta-row">
              <div className="hba-meta-item"><span>Reg No :</span><input className="hba-input" value={form.regNo} readOnly /></div>
              <div className="hba-meta-item"><span>Branch</span><input className="hba-input" value={form.branch} readOnly /></div>
              <div className="hba-meta-item"><span>Date</span><input className="hba-input" value={form.date} readOnly /></div>
            </div>
            <div className="hba-pad">
              <div className="hba-line">
                <span className="hba-line-label">Mr. / Mrs. / Master. / Ms.</span>
                <input className="hba-input hba-input-dotted" value={form.patientName} readOnly />
              </div>
              <div className="hba-line hba-line-multi">
                <span className="hba-mini">Date of Birth:</span>
                <input className="hba-input hba-input-dotted" style={{ flex: 1 }} value={form.dob} readOnly />
                <span className="hba-mini">Age:</span>
                <input className="hba-input hba-input-dotted" style={{ width: 70 }} value={form.age} readOnly />
                <span className="hba-mini">Sex:</span>
                <label className="hba-cb-inline"><input type="checkbox" checked={form.sex === "Male"} disabled readOnly /> Male</label>
                <label className="hba-cb-inline"><input type="checkbox" checked={form.sex === "Female"} disabled readOnly /> Female</label>
              </div>
              <div className="hba-line">
                <span className="hba-line-label">Address for Communication:</span>
                <input className="hba-input hba-input-dotted" value={form.address1} readOnly />
              </div>
              <input className="hba-input hba-input-dotted hba-input-full" value={form.address2} readOnly />
              <input className="hba-input hba-input-dotted hba-input-full" value={form.address3} readOnly />
              <input className="hba-input hba-input-dotted hba-input-full" value={form.address4} readOnly />
              <div className="hba-line">
                <span className="hba-line-label">Contact Number</span>
                <input className="hba-input hba-input-box" value={form.contact} readOnly />
              </div>
              <div className="hba-line">
                <span className="hba-line-label">E Mail</span>
                <input className="hba-input hba-input-box" value={form.email} readOnly />
              </div>
            </div>
          </section>

          {/* ─────── PAGE 2 — Chief + Disorders ─────── */}
          <section className="hba-page hba-page-common">
            <table className="hba-cc-tbl">
              <tbody>
                <tr>
                  <td className="hba-cc-main">
                    <div className="hba-cc-lbl">Chief Complaints:</div>
                    <textarea className="hba-textarea hba-textarea-cc" value={form.chiefComplaints} readOnly />
                  </td>
                  <td className="hba-cc-dur">
                    <div className="hba-cc-lbl">Duration:</div>
                    <textarea className="hba-textarea hba-textarea-cc" value={form.chiefDuration} readOnly />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="hba-section-title">Associate Complaints:</div>
            <table className="hba-disorder-tbl">
              <tbody>
                <DisorderRow title="Digestive Disorders:" sub="Rasa" group="digestive" items={["Indigestion","Acidity","Ulcer","IBS","Gastritis","GERD"]} even={false} />
                <DisorderRow title="Hematological Disorders:" sub="Raktha" group="hematological" items={["Anemia","Hypertension","Varicose vein","Cholesterol","Triglyceride"]} even={true} />
                <DisorderRow title="Muscular Disorders:" sub="Mamsa" group="muscular" items={["Obesity","Weight Loss","Muscle pain","Debility"]} even={false} />
                <DisorderRow title="Skin Disorders:" sub="Medas" group="skin" items={["Itching","Discolouration","Vitiligo","Eczema","Psoriasis"]} even={true} />
                <DisorderRow title="Skeletal Disorders:" sub="Asthi" group="skeletal" items={["Osteo Arthritis","Rheumatoid arthritis","Disc prolapse","Lumbar Spondylosis","Cervical Spondylosis","Sciatica","Gout"]} even={false} />
                <DisorderRow title="Nervous Disorder:" sub="Majja" group="nervous" items={["Parkinson's disease","Paralysis","Alzheimer's disease","Immunity"]} even={true} />
              </tbody>
            </table>
          </section>

          {/* ─────── PAGE 3 — More Disorders + Rehab ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-section-title">Associate Complaints:</div>
            <table className="hba-disorder-tbl">
              <tbody>
                <DisorderRow title="Anorectal Disorders:" sub="Mooladhara" group="anorectal" items={["Piles","Fistula","Fissure in ano"]} even={false} />
                <DisorderRow title="Reproductive System Disorders:" sub="Swadhisthana" group="reproductive" items={["Male Infertility","Prostate Disorders","Erectile dysfunction","Female Infertility","Leucorrhoea","Amenorrhoea","Dysmenorrhoea","Irregular menses","PCOD","Fibroid","Endometriosis","Monopausal Syndrome"]} even={true} />
                <DisorderRow title="Kidney Disorders:" sub="Manipura" group="kidney" items={["Kidney stone","Kidney failure","Diabetes Mellitus","Diabetes neuritis","Diabetes nephritis","Diabetic foot ulcer"]} even={false} />
                <DisorderRow title="Cardiac / Hepatic Disorders:" sub="Anahata" group="cardiac" items={["Angina Pectoris","Coronary Artery diseases","Cardiomyopathy","Atherosclerosis","Fatty liver","Liver Cirrhosis"]} even={true} />
                <DisorderRow title="Neck & thoracic related Disorders:" sub="Vishuddhi" group="neckThoracic" items={["Thyroid","Asthma","Wheezing"]} even={false} />
                <DisorderRow title="Head & ENT Disorders:" sub="Ajna" group="headEnt" items={["ENT","Sinusitis","Migraine","Anxiety","Stress","ADHD","Hair Loss"]} even={true} />
              </tbody>
            </table>
            <div className="hba-rehab">
              <div className="hba-section-title">REHAB:</div>
              <div className="hba-rehab-grid">
                <div>
                  <span className="hba-rehab-lbl">Cancer</span>
                  <input className="hba-input hba-input-box" value={form.rehabCancer} readOnly />
                </div>
                <div>
                  <span className="hba-rehab-lbl">Asadhya rogas</span>
                  <input className="hba-input hba-input-box" value={form.rehabAsadhya} readOnly />
                </div>
              </div>
            </div>
          </section>

          {/* ─────── PAGE 4 — Personal History + OB/GYN ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-section-title">Personal History:</div>
            <table className="hba-ph-tbl">
              <tbody>
                <tr>
                  <td className="hba-ph-k">Appetite</td><td>:</td>
                  <td>
                    <div className="hba-cb-row">
                      {["Poor","Good","High"].map(opt => (
                        <label className="hba-cb-inline" key={opt}>
                          <input type="checkbox" checked={form.appetite === opt} disabled readOnly /> {opt}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Bowel</td><td>:</td>
                  <td>
                    <div className="hba-cb-row">
                      <label className="hba-cb-inline"><input type="checkbox" checked={!!form.bowel?.Regular} disabled readOnly /> Regular</label>
                      <label className="hba-cb-inline"><input type="checkbox" checked={!!form.bowel?.Constipated} disabled readOnly /> Constipated</label>
                      <input className="hba-input hba-input-box" style={{ width: 120 }} value={form.bowel?._other || ""} readOnly />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Micturation</td><td>:</td>
                  <td>
                    <div className="hba-cb-row">
                      {["Normal","Increased frequency","Decreased freqency","Burning sensation","Incontenence"].map(opt => (
                        <label className="hba-cb-inline" key={opt}>
                          <input type="checkbox" checked={!!form.micturation?.[opt]} disabled readOnly /> {opt}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Sleep</td><td>:</td>
                  <td>
                    <div className="hba-cb-row">
                      <label className="hba-cb-inline"><input type="checkbox" checked={!!form.sleep?.Sound} disabled readOnly /> Sound</label>
                      <label className="hba-cb-inline"><input type="checkbox" checked={!!form.sleep?.Disturbed} disabled readOnly /> Disturbed</label>
                      <input className="hba-input hba-input-box" style={{ width: 130 }} value={form.sleep?._other || ""} readOnly />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Pain<br /><span className="hba-mini-grey">(if any)</span></td><td>:</td>
                  <td><input className="hba-input hba-input-box hba-input-full" value={form.pain} readOnly /></td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Stress</td><td>:</td>
                  <td>
                    <div className="hba-cb-row">
                      {["Yes","No","Personal","Professional"].map(opt => (
                        <label className="hba-cb-inline" key={opt}>
                          <input type="checkbox" checked={!!form.stress?.[opt]} disabled readOnly /> {opt}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="hba-section-title">OB / GYN History:</div>
            <table className="hba-ph-tbl">
              <tbody>
                <tr><td className="hba-ph-k">LMP</td><td><input className="hba-input hba-input-box" style={{ width: 200 }} value={form.lmp} readOnly /></td></tr>
                <tr><td className="hba-ph-k">Menstrual cycle</td><td><input className="hba-input hba-input-box" style={{ width: 200 }} value={form.menstrualCycle} readOnly /></td></tr>
                <tr><td className="hba-ph-k">Menstrual flow</td><td><input className="hba-input hba-input-box" style={{ width: 200 }} value={form.menstrualFlow} readOnly /></td></tr>
                <tr>
                  <td className="hba-ph-k">Dysmenorrhoea</td>
                  <td>
                    <div className="hba-cb-row">
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.dysmenorrhoea === "Yes"} disabled readOnly /> Yes</label>
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.dysmenorrhoea === "No"} disabled readOnly /> No</label>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Leucorrhoea</td>
                  <td>
                    <div className="hba-cb-row">
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.leucorrhoea === "Yes"} disabled readOnly /> Yes</label>
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.leucorrhoea === "No"} disabled readOnly /> No</label>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Delivery</td>
                  <td>
                    <div className="hba-cb-row">
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.delivery === "Cesarean"} disabled readOnly /> Cesarean</label>
                      <label className="hba-cb-inline"><input type="checkbox" checked={form.delivery === "Normal"} disabled readOnly /> Normal</label>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="hba-block-title">Specific complaints <span className="hba-mini-grey">(if any):</span></div>
            <textarea className="hba-textarea hba-textarea-block" value={form.specificComplaints} readOnly />
          </section>

          {/* ─────── PAGE 5 — Past History + Family ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-section-title">Past History:</div>
            <table className="hba-past-tbl">
              <tbody>
                {[["Hypertension","pastHypertension"],["Diabetes","pastDiabetes"],["Piles / Constipation","pastPiles"],["Thyroid","pastThyroid"]].map(([lbl, key]) => (
                  <tr key={key}>
                    <td className="hba-ph-k">{lbl}</td>
                    <td>
                      <div className="hba-cb-row">
                        <label className="hba-cb-inline"><input type="checkbox" checked={form[key] === "Yes"} disabled readOnly /> Yes</label>
                        <label className="hba-cb-inline"><input type="checkbox" checked={form[key] === "No"} disabled readOnly /> No</label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="hba-block-title">Past Duration:</div>
            <input className="hba-input hba-input-box hba-input-full" value={form.pastDuration} readOnly />
            <div className="hba-block-title" style={{ marginTop: 16 }}>Other diseases</div>
            <textarea className="hba-textarea hba-textarea-block" value={form.otherDiseases} readOnly />
            <input className="hba-input hba-input-box hba-input-full" placeholder="Duration" value={form.otherDuration} readOnly />
            <div className="hba-section-title" style={{ marginTop: 18 }}>Family History:</div>
            <textarea className="hba-textarea hba-textarea-tall" value={form.familyHistory} readOnly />
          </section>

          {/* ─────── PAGE 6 — Surgical / Medical / Diet ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-section-title">Surgical History:</div>
            <textarea className="hba-textarea hba-textarea-block" value={form.surgicalHistory} readOnly />
            <div className="hba-section-title">Medical History / Current Prescription:</div>
            <textarea className="hba-textarea hba-textarea-block" value={form.medicalHistory} readOnly />
            <div className="hba-section-title">Diet History:</div>
            <textarea className="hba-textarea hba-textarea-block" value={form.dietHistory} readOnly />
            <div className="hba-block-title">Water Intake:</div>
            <textarea className="hba-textarea hba-textarea-short" value={form.waterIntake} readOnly />
            <div className="hba-block-title">Addictions:</div>
            <textarea className="hba-textarea hba-textarea-short" value={form.addictions} readOnly />
            <div className="hba-line hba-line-multi" style={{ marginTop: 12 }}>
              <span className="hba-section-h-inline">Physical activity:</span>
              <label className="hba-cb-inline"><input type="checkbox" checked={form.physicalActivity === "Yes"} disabled readOnly /> Yes</label>
              <label className="hba-cb-inline"><input type="checkbox" checked={form.physicalActivity === "No"} disabled readOnly /> No</label>
              <input className="hba-input hba-input-box" style={{ width: 140 }} value={form.physicalActivityDuration} readOnly />
            </div>
          </section>

          {/* ─────── PAGE 7 — Medical Assessment ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-section-title">Medical Assessment:</div>
            <table className="hba-ma-tbl">
              <tbody>
                <tr>
                  <td className="hba-ph-k">Height</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.height} readOnly /></td>
                  <td className="hba-ph-k">Blood Pressure</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.bp} readOnly /></td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Weight</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.weight} readOnly /></td>
                  <td className="hba-ph-k">Pulse rate</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.pulse} readOnly /></td>
                </tr>
                <tr>
                  <td className="hba-ph-k">Skin</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.skin2} readOnly /></td>
                  <td className="hba-ph-k">Vision</td>
                  <td><input className="hba-input hba-input-box" style={{ width: 130 }} value={form.vision} readOnly /></td>
                </tr>
              </tbody>
            </table>
            <div className="hba-section-title">Physical examination:</div>
            <textarea className="hba-textarea hba-textarea-tall" value={form.physicalExam} readOnly />
            <div className="hba-section-title">Lab investigation:</div>
            <textarea className="hba-textarea hba-textarea-tall" value={form.labInvestigation} readOnly />
            <div className="hba-section-title">Diagnosis:</div>
            <textarea className="hba-textarea hba-textarea-block" value={form.diagnosis} readOnly />
          </section>

          {/* ─────── PAGE 8 — Ayurvedic + Signatures ─────── */}
          <section className="hba-page hba-page-common">
            <div className="hba-ayur-row" data-bg="odd">
              <div className="hba-ayur-lbl">Dosha :</div>
              <div className="hba-cb-grid">
                {Object.keys(form.dosha).map(k => (
                  <label className="hba-cb" key={k}><input type="checkbox" checked={!!form.dosha[k]} disabled readOnly /><span>{k}</span></label>
                ))}
              </div>
            </div>
            <div className="hba-ayur-row" data-bg="even">
              <div className="hba-ayur-lbl">Dhatu :</div>
              <div className="hba-cb-grid">
                {Object.keys(form.dhatu).map(k => (
                  <label className="hba-cb" key={k}><input type="checkbox" checked={!!form.dhatu[k]} disabled readOnly /><span>{k}</span></label>
                ))}
              </div>
            </div>
            <div className="hba-ayur-row" data-bg="odd">
              <div className="hba-ayur-lbl">Chakra:</div>
              <div className="hba-cb-grid">
                {Object.keys(form.chakra).map(k => (
                  <label className="hba-cb" key={k}><input type="checkbox" checked={!!form.chakra[k]} disabled readOnly /><span>{k}</span></label>
                ))}
              </div>
            </div>
            <div className="hba-ayur-row" data-bg="even">
              <div className="hba-ayur-lbl">Marma / Varma:</div>
              <div className="hba-marma-grid">
                {form.marma.map((v, i) => (
                  <input key={i} className="hba-input hba-input-box" value={v} readOnly />
                ))}
              </div>
            </div>
            <div className="hba-ayur-row" data-bg="odd">
              <div className="hba-ayur-lbl">Kosha:</div>
              <div className="hba-cb-grid">
                {Object.keys(form.kosha).map(k => (
                  <label className="hba-cb" key={k}><input type="checkbox" checked={!!form.kosha[k]} disabled readOnly /><span>{k}</span></label>
                ))}
              </div>
            </div>
            <div className="hba-sign-row">
              <div>
                <div className="hba-sign-lbl">Doctor in Chief:</div>
                <input className="hba-input hba-sign-line" value={form.doctorChief} readOnly />
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="hba-sign-lbl">Associate Doctor:</div>
                <input className="hba-input hba-sign-line" value={form.associateDoctor} readOnly />
              </div>
            </div>
          </section>

        </div>

        {/* Bottom bar */}
        <div className="hba-bottom-bar">
          <button className="hba-btn hba-btn--ghost" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i> Close
          </button>
          <button className="hba-btn hba-btn--ghost" onClick={handleDownloadPdf}>
            <i className="fa-solid fa-file-pdf"></i> Download PDF
          </button>
        </div>
      </div>

      {/* HBA Styles */}
      <style>{`
        :root {
          --hba-gold: #b8860b;
          --hba-gold-lt: #cea465;
          --hba-cream: #fdf8ee;
          --hba-cream-2: #f5edd8;
          --hba-cream-3: #f5e8c0;
          --hba-cream-4: #fdf3e3;
        }
        .hba-overlay {
          position: fixed; inset: 0; z-index: 1090;
          background: rgba(20,30,48,0.85);
          backdrop-filter: blur(6px);
          display: flex; flex-direction: column;
          animation: hba-fadein .2s ease;
        }
        @keyframes hba-fadein { from { opacity:0; } to { opacity:1; } }
        .hba-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 22px; gap: 14px; flex-wrap: wrap;
          background: rgba(255,255,255,0.98);
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
          box-shadow: 0 2px 14px rgba(0,0,0,.08);
        }
        .hba-toolbar-left { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; min-width: 0; }
        .hba-toolbar-title {
          margin: 0; font-size: 17px; font-weight: 700; color: #111827;
          display: flex; align-items: center; gap: 8px;
        }
        .hba-toolbar-title i {
          width: 34px; height: 34px; border-radius: 10px;
          background: linear-gradient(135deg, var(--hba-gold), #d4a017); color: #fff;
          display: flex; align-items: center; justify-content: center; font-size: 15px;
        }
        .hba-toolbar-sub { font-size: 12.5px; color: #6b7280; }
        .hba-toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .hba-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 8px;
          font-size: 13px; font-weight: 600; cursor: pointer;
          border: 1.5px solid transparent;
          transition: transform .1s, opacity .15s;
          white-space: nowrap;
        }
        .hba-btn:hover { transform: translateY(-1px); }
        .hba-btn--ghost { background: #fff; color: var(--hba-gold); border-color: var(--hba-gold-lt); }
        .hba-btn--ghost:hover { background: #fef9e7; }
        .hba-btn--close { background: #f3f4f6; color: #4b5563; width: 38px; height: 38px; padding: 0; justify-content: center; }
        .hba-btn--close:hover { background: #fee2e2; color: #991b1b; }
        .hba-scroll {
          flex: 1; overflow-y: auto;
          background: linear-gradient(180deg, rgba(206,164,101,0.08), rgba(206,164,101,0.02));
        }
        .hba-page-wrap {
          max-width: 980px; margin: 24px auto; padding: 0 18px 24px;
          font-family: Georgia, "Times New Roman", Times, serif; color: #222;
        }
        .hba-page {
          background: #fff; border: 1px solid var(--hba-gold-lt);
          border-radius: 8px; padding: 24px; margin-bottom: 22px;
          box-shadow: 0 4px 16px rgba(0,0,0,.06);
        }
        .hba-logo-strip { text-align: center; padding: 8px 0 18px; }
        .hba-logo-text { font-size: 22px; font-weight: 700; color: var(--hba-gold); letter-spacing: .03em; }
        .hba-section-h2 { color: #aa804c; font-size: 22px; margin: 0; }
        .hba-row-between { display: flex; justify-content: space-between; align-items: center; padding: 0 8px; gap: 12px; flex-wrap: wrap; }
        .hba-meta-row {
          background: #f5ede0; padding: 12px; margin-top: 12px;
          display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; border-radius: 4px;
        }
        .hba-meta-item { display: flex; align-items: center; gap: 8px; font-size: 14px; }
        .hba-meta-item span { font-weight: 600; min-width: 60px; }
        .hba-pad { padding: 14px 8px; }
        .hba-line { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
        .hba-line-multi { gap: 8px; }
        .hba-line-label { font-size: 14px; font-weight: 500; min-width: 100px; }
        .hba-mini { font-size: 13px; }
        .hba-mini-grey { font-size: 11px; color: #6b7280; }
        .hba-input {
          font-family: inherit; background: transparent; border: 0;
          padding: 4px 6px; font-size: 14px; color: #222;
          outline: none; flex: 1; min-width: 80px;
        }
        .hba-input:read-only { color: #555; cursor: default; }
        .hba-input-dotted { border-bottom: 1px dashed var(--hba-gold-lt); }
        .hba-input-box { border: 1px dashed var(--hba-gold-lt); border-radius: 4px; padding: 6px 10px; background: #fffdf6; }
        .hba-input-full { width: 100%; flex: none; display: block; margin-bottom: 8px; }
        .hba-input-dur { background: var(--hba-cream-3) !important; border: 1px solid #d4b87a !important; padding: 6px 8px !important; width: 100%; font-size: 12px; }
        .hba-cb-inline { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; cursor: default; margin: 0; }
        .hba-cc-tbl { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
        .hba-cc-main, .hba-cc-dur { border: 1px solid #d4b87a; padding: 10px 12px; vertical-align: top; }
        .hba-cc-main { width: 70%; background: #fdf0d0; }
        .hba-cc-dur { width: 30%; background: var(--hba-cream-3); }
        .hba-cc-lbl { font-size: 13px; font-weight: bold; color: var(--hba-gold); margin-bottom: 8px; }
        .hba-textarea {
          width: 100%; font-family: inherit; background: transparent;
          border: 0; color: #222; font-size: 13.5px; padding: 4px;
          resize: vertical; outline: none;
        }
        .hba-textarea-cc { min-height: 70px; }
        .hba-textarea-block { min-height: 110px; background: var(--hba-cream-4) !important; border: 1px solid #d4b87a !important; padding: 10px !important; border-radius: 4px; }
        .hba-textarea-tall { min-height: 180px; background: var(--hba-cream-4) !important; border: 1px solid #d4b87a !important; padding: 10px !important; border-radius: 4px; }
        .hba-textarea-short { min-height: 50px; background: var(--hba-cream-4) !important; border: 1px solid #d4b87a !important; padding: 8px !important; border-radius: 4px; }
        .hba-section-title { font-weight: bold; font-size: 14px; color: var(--hba-gold); border-bottom: 2px solid var(--hba-gold); padding-bottom: 4px; margin: 16px 0 14px; display: inline-block; min-width: 50%; }
        .hba-section-h-inline { font-weight: bold; font-size: 13px; color: var(--hba-gold); }
        .hba-block-title { font-weight: bold; font-size: 13px; color: #3a2a0e; margin: 12px 0 8px; }
        .hba-disorder-tbl, .hba-ph-tbl, .hba-past-tbl, .hba-ma-tbl { width: 100%; border-collapse: collapse; }
        .hba-cell-label, .hba-cell-items, .hba-cell-duration { border: 1px solid #e0cfa0; padding: 10px; vertical-align: middle; }
        .hba-cell-label { width: 22%; }
        .hba-cell-items { width: 60%; }
        .hba-cell-duration { width: 18%; background: var(--hba-cream-3); vertical-align: top; }
        .hba-cell-label[data-even="false"], .hba-cell-items[data-even="false"] { background: var(--hba-cream); }
        .hba-cell-label[data-even="true"], .hba-cell-items[data-even="true"] { background: var(--hba-cream-2); }
        .hba-d-title { font-weight: bold; font-size: 13px; color: #5a3e1b; display: block; }
        .hba-d-sub { font-size: 10px; color: #a08050; font-style: italic; }
        .hba-d-dur-lbl { font-weight: bold; font-size: 10px; color: #5a3e1b; display: block; margin-bottom: 4px; text-align: right; }
        .hba-cb-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 6px 14px; }
        .hba-cb-row { display: flex; gap: 18px; flex-wrap: wrap; align-items: center; margin-bottom: 6px; }
        .hba-cb { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #3a2a0e; margin: 0; }
        .hba-rehab { margin-top: 18px; border-top: 1px solid #e0cfa0; padding-top: 12px; }
        .hba-rehab-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 8px; }
        .hba-rehab-lbl { font-weight: bold; font-size: 13px; color: #5a3e1b; display: block; margin-bottom: 6px; }
        .hba-ph-tbl tr { border-bottom: 1px solid #f0dfa8; }
        .hba-ph-tbl td { padding: 10px 6px; font-size: 13px; vertical-align: middle; }
        .hba-ph-k { width: 25%; font-weight: bold; color: #3a2a0e; }
        .hba-past-tbl tr { border-bottom: 1px solid #f0dfa8; }
        .hba-past-tbl td { padding: 9px 4px; font-size: 13px; }
        .hba-ma-tbl tr { border-bottom: 1px solid #f0dfa8; }
        .hba-ma-tbl td { padding: 9px 4px; font-size: 13px; }
        .hba-ayur-row { display: grid; grid-template-columns: 22% 1fr; padding: 14px 12px; gap: 16px; border: 1px solid #e8d9b8; margin-bottom: 8px; border-radius: 4px; align-items: start; }
        .hba-ayur-row[data-bg="odd"] { background: var(--hba-cream); }
        .hba-ayur-row[data-bg="even"] { background: var(--hba-cream-2); }
        .hba-ayur-lbl { font-weight: bold; font-size: 14px; color: #5a3e1b; }
        .hba-marma-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; }
        .hba-sign-row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 24px; border-top: 1px solid #e8d9b8; padding-top: 18px; }
        .hba-sign-lbl { font-weight: bold; font-size: 13px; color: var(--hba-gold); margin-bottom: 8px; }
        .hba-sign-line { width: 200px; border-bottom: 1px solid var(--hba-gold) !important; padding: 8px 4px !important; }
        .hba-bottom-bar { display: flex; gap: 10px; justify-content: center; padding: 14px 18px; background: rgba(255,255,255,0.96); border-top: 1px solid #e5e7eb; flex-shrink: 0; flex-wrap: wrap; }
        @media print {
          .main-wrapper, #videoModal { display: none !important; }
          .hba-overlay { position: absolute !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: auto !important; background: #fff !important; overflow: visible !important; height: auto !important; }
          .hba-toolbar, .hba-bottom-bar { display: none !important; }
          .hba-scroll { overflow: visible !important; height: auto !important; flex: none !important; background: none !important; }
          .hba-page { box-shadow: none !important; page-break-after: always; break-after: page; }
          .hba-page:last-of-type { page-break-after: avoid; break-after: avoid; }
          .hba-page-wrap { margin: 0 !important; max-width: 100% !important; padding: 0 !important; }
        }
        @media (max-width: 600px) {
          .hba-meta-row { grid-template-columns: 1fr; }
          .hba-cb-grid { grid-template-columns: 1fr; }
          .hba-rehab-grid { grid-template-columns: 1fr; }
          .hba-ayur-row { grid-template-columns: 1fr; }
          .hba-marma-grid { grid-template-columns: 1fr; }
          .hba-sign-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CUSTOM CONFIRM DIALOG
═══════════════════════════════════════════════════════════════ */
const ConfirmDialog = ({
  open, title, message,
  confirmLabel = "Confirm", cancelLabel = "Cancel",
  confirmVariant = "danger", hideCancel = false,
  onConfirm, onCancel,
}) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : confirmVariant === "warning" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        {message ? <p className="cdlg-message">{message}</p> : null}
        <div className="cdlg-actions">
          {!hideCancel && (
            <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>{cancelLabel}</button>
          )}
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   VIDEO CALL SUB-COMPONENTS
═══════════════════════════════════════════════════════════════ */
const VcTopBar = ({ status, duration, doctorName, apptType, onClose }) => {
  const statusMeta = {
    connecting: { dot: "#f59e0b", label: "Connecting…", pulse: true },
    live: { dot: "#10b981", label: "Live", pulse: false },
    reconnecting: { dot: "#ef4444", label: "Reconnecting…", pulse: true },
  }[status] || { dot: "#6b7280", label: "Idle", pulse: false };
  const typeInfo = getAppointmentTypeLabel(apptType);
  return (
    <div className="vc-topbar">
      <div className="vc-topbar-left">
        <span className={`vc-status-dot ${statusMeta.pulse ? "vc-pulse" : ""}`} style={{ background: statusMeta.dot }} />
        <div className="vc-topbar-info">
          <div className="vc-topbar-title">{doctorName || "Video Consultation"}</div>
          <div className="vc-topbar-sub">
            {statusMeta.label}
            {apptType ? <span className={`vc-topbar-type ${typeInfo.cls}`}><i className={typeInfo.icon} /> {typeInfo.label}</span> : null}
          </div>
        </div>
      </div>
      <div className="vc-topbar-right">
        <span className="vc-duration"><i className="fa-regular fa-clock" /> {duration}</span>
        <button className="vc-topbar-close" onClick={onClose} title="Close"><i className="fa-solid fa-xmark" /></button>
      </div>
    </div>
  );
};

const VcControlsBar = ({ isMuted, isCameraOff, onToggleMic, onToggleCam, onSwitchCam, onSwap, onChat, onEnd, chatUnread, isMobile, disabled }) => {
  const Btn = ({ onClick, icon, label, active = true, danger = false, big = false, badge = 0, dimmed = false }) => (
    <div className="vc-ctrl-wrap">
      <button onClick={onClick} disabled={dimmed}
        className={`vc-ctrl-btn ${danger ? "vc-ctrl-danger" : ""} ${!active ? "vc-ctrl-off" : ""} ${big ? "vc-ctrl-big" : ""} ${dimmed ? "vc-ctrl-dimmed" : ""}`}
        title={label} aria-label={label}>
        <i className={`fa-solid ${icon}`} />
        {badge > 0 && <span className="vc-ctrl-badge">{badge > 9 ? "9+" : badge}</span>}
      </button>
      <span className="vc-ctrl-label">{label}</span>
    </div>
  );
  return (
    <div className="vc-controls">
      <Btn onClick={onToggleMic} icon={isMuted ? "fa-microphone-slash" : "fa-microphone"} label={isMuted ? "Unmute" : "Mute"} active={!isMuted} dimmed={disabled} />
      <Btn onClick={onToggleCam} icon={isCameraOff ? "fa-video-slash" : "fa-video"} label={isCameraOff ? "Start cam" : "Camera"} active={!isCameraOff} dimmed={disabled} />
      {isMobile && <Btn onClick={onSwitchCam} icon="fa-camera-rotate" label="Flip" dimmed={disabled} />}
      <Btn onClick={onSwap} icon="fa-arrows-left-right-to-line" label="Swap" dimmed={disabled} />
      <Btn onClick={onChat} icon="fa-comment-dots" label="Chat" badge={chatUnread} />
      <Btn onClick={onEnd} icon="fa-phone-slash" label="End call" danger big />
    </div>
  );
};

const VcChatPanel = ({ open, isMobile, messages, input, setInput, onSend, onClose, peerName }) => {
  const bottomRef = useRef(null);
  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, open]);
  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } };
  return (
    <aside className={`vc-chat ${open ? "vc-chat-open" : ""} ${isMobile ? "vc-chat-mobile" : ""}`}>
      <div className="vc-chat-head">
        <div>
          <div className="vc-chat-title">Chat</div>
          <div className="vc-chat-sub">with {peerName || "doctor"}</div>
        </div>
        <button className="vc-chat-close" onClick={onClose} title="Close chat"><i className="fa-solid fa-xmark" /></button>
      </div>
      <div className="vc-chat-body">
        {messages.length === 0 ? (
          <div className="vc-chat-empty">
            <i className="fa-regular fa-comments" /><p>No messages yet.</p>
            <span>Send a message to start the conversation.</span>
          </div>
        ) : messages.map(m => (
          <div key={m.id} className={`vc-msg vc-msg-${m.sender}`}>
            <div className="vc-msg-bubble">{m.text}</div>
            <div className="vc-msg-time">{m.time}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="vc-chat-input">
        <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder="Type a message…" maxLength={1000} />
        <button onClick={onSend} disabled={!input.trim()} title="Send"><i className="fa-solid fa-paper-plane" /></button>
      </div>
    </aside>
  );
};

/* ═══════════════════════════════════════════════════════════════
   VALIDATION HELPERS
═══════════════════════════════════════════════════════════════ */
const validateAddress = (form) => {
  const errors = {};
  const house = (form.house || "").trim();
  if (!house) errors.house = "House / flat number is required";
  else if (house.length > 50) errors.house = "Maximum 50 characters allowed";
  const street = (form.address || "").trim();
  if (!street) errors.address = "Street address is required";
  else if (street.length < 5) errors.address = "Street address must be at least 5 characters";
  else if (street.length > 150) errors.address = "Maximum 150 characters allowed";
  if (form.landmark && form.landmark.trim().length > 80) errors.landmark = "Maximum 80 characters allowed";
  const city = (form.city || "").trim();
  if (!city) errors.city = "City is required";
  else if (city.length < 2) errors.city = "City name is too short";
  else if (!/^[a-zA-Z\s.'-]+$/.test(city)) errors.city = "City can only contain letters, spaces and . ' -";
  const state = (form.state || "").trim();
  if (!state) errors.state = "State is required";
  else if (state.length < 2) errors.state = "State name is too short";
  else if (!/^[a-zA-Z\s.'-]+$/.test(state)) errors.state = "State can only contain letters, spaces and . ' -";
  const pin = (form.pincode || "").trim();
  if (!pin) errors.pincode = "Pincode is required";
  else if (!/^\d{6}$/.test(pin)) errors.pincode = "Enter a valid 6-digit pincode";
  else if (pin.startsWith("0")) errors.pincode = "Pincode cannot start with 0";
  const recipient = (form.recipient || "").trim();
  if (!recipient) errors.recipient = "Recipient name is required";
  else if (recipient.length < 3) errors.recipient = "Name must be at least 3 characters";
  else if (recipient.length > 60) errors.recipient = "Maximum 60 characters allowed";
  else if (!/^[a-zA-Z\s.'-]+$/.test(recipient)) errors.recipient = "Name can only contain letters, spaces and . ' -";
  const phone = (form.phone || "").trim();
  if (!phone) errors.phone = "Phone number is required";
  else if (!/^\d{10}$/.test(phone)) errors.phone = "Phone must be exactly 10 digits";
  else if (!/^[6-9]/.test(phone)) errors.phone = "Must start with 6, 7, 8 or 9";
  if (!["self", "other"].includes(form.orderFor)) errors.orderFor = "Please select who you're ordering for";
  return errors;
};

const validateAddressField = (name, value, form) => {
  const tempForm = { ...form, [name]: value };
  const all = validateAddress(tempForm);
  return all[name] || "";
};

const validatePassword = (data) => {
  const errors = {};
  if (!data.current_password) errors.current_password = "Current password is required";
  if (!data.new_password) errors.new_password = "New password is required";
  else if (data.new_password.length < 6) errors.new_password = "Password must be at least 6 characters";
  if (!data.confirm_password) errors.confirm_password = "Please confirm your new password";
  else if (data.new_password !== data.confirm_password) errors.confirm_password = "Passwords do not match";
  return errors;
};

const getPasswordStrength = (pwd) => {
  const checks = {
    length: pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const strength =
    score <= 2 ? { label: "Weak",   color: "#ef4444", bars: 1 } :
    score === 3 ? { label: "Medium", color: "#f59e0b", bars: 2 } :
                  { label: "Strong", color: "#10b981", bars: 3 };
  return { checks, score, strength };
};

const FieldError = ({ msg }) =>
  msg ? (
    <span className="mp-field-error">
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {msg}
    </span>
  ) : null;

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Checkout = () => {
  const navigate = useNavigate();

  let user = null;
  try {
    const data = localStorage.getItem("user");
    user = data ? JSON.parse(data) : null;
    if (!user) {
      ["authToken","config_id","user_id","user","name","role"].forEach(k => localStorage.removeItem(k));
      setTimeout(() => navigate("/index", { replace: true }), 100);
    }
  } catch (e) {
    ["authToken","config_id","user_id","user","name","role"].forEach(k => localStorage.removeItem(k));
    setTimeout(() => navigate("/index", { replace: true }), 100);
  }

  const [cartCount, setCartCount] = useState(0);
  const [cartAmount, setCartAmount] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [userId, setUserId] = useState(localStorage.getItem("user_id"));
  const [userData, setUserData] = useState(user);
  const [editAddress, setEditAddress] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchOrderId, setSearchOrderId] = useState("");
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [tracks, setTrackList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null); // { orderId, productId, productName, productImage }
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHoverRating, setReviewHoverRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewsEnabled, setReviewsEnabled] = useState(false); // admin-controlled, off by default (product reviews)
  const [doctorReviewsEnabled, setDoctorReviewsEnabled] = useState(false); // admin-controlled, off by default (doctor reviews - independent switch)
  const [showDoctorReviewModal, setShowDoctorReviewModal] = useState(false);
  const [doctorReviewTarget, setDoctorReviewTarget] = useState(null); // { doctorId, doctorName }
  const [doctorReviewRating, setDoctorReviewRating] = useState(0);
  const [doctorReviewHoverRating, setDoctorReviewHoverRating] = useState(0);
  const [doctorReviewComment, setDoctorReviewComment] = useState("");
  const [submittingDoctorReview, setSubmittingDoctorReview] = useState(false);
  const [reviewedDoctorIds, setReviewedDoctorIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("reviewed_doctor_ids") || "[]"); }
    catch { return []; }
  });
  const [reviewedKeys, setReviewedKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem("reviewed_order_products") || "[]"); }
    catch { return []; }
  });
  const [room, setRoom] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const toggleMobileAccordion = (key) => setMobileAccordion(p => p === key ? null : key);
  const [activeTab, setActiveTab] = useState("appointments");
  const [addressErrors, setAddressErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [addrSearch, setAddrSearch] = useState("");
  const [wishSearch, setWishSearch] = useState("");
  const [apptSearch, setApptSearch] = useState("");
  const [trackSearch, setTrackSearch] = useState("");
  const initialAddressForm = { house: "", address: "", landmark: "", city: "", pincode: "", state: "", recipient: "", phone: "", orderFor: "self" };
  const [addressForm, setAddressForm] = useState(initialAddressForm);
  const [isSwapped, setIsSwapped] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatUnread, setChatUnread] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [callSeconds, setCallSeconds] = useState(0);
  const [facingMode, setFacingMode] = useState("user");
  const [pipPos, setPipPos] = useState({ x: 0, y: 0 });
  const [activeDoctor, setActiveDoctor] = useState("Doctor");
  const [activeApptType, setActiveApptType] = useState(null);
  const [activeApptId, setActiveApptId] = useState(null);
  const [isMobileVc, setIsMobileVc] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);

  // HBA State
  const [hbaOpen, setHbaOpen] = useState(false);
  const [hbaMode, setHbaMode] = useState("view");
  const [hbaTarget, setHbaTarget] = useState(null);

  const openHbaForm = useCallback((row, mode = "view") => {
    setHbaTarget(row);
    setHbaMode(mode);
    setHbaOpen(true);
  }, []);

  const closeHbaForm = useCallback(() => {
    setHbaOpen(false);
    setHbaTarget(null);
  }, []);

  const dragStateRef = useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const dataTrackRef = useRef(null);
  const roomRef = useRef(null);
  const contentRef = useRef(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "",
    confirmLabel: "Confirm", cancelLabel: "Cancel",
    confirmVariant: "danger", hideCancel: false, onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", confirmVariant = "danger", hideCancel = false, onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, cancelLabel, confirmVariant, hideCancel, onConfirm });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog(p => ({ ...p, open: false, onConfirm: null }));
  }, []);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) { deviceId = uuidv4(); localStorage.setItem("device_id", deviceId); }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(p => ({ ...p, [name]: value }));
    if (passwordErrors[name]) setPasswordErrors(p => ({ ...p, [name]: "" }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUserData(p => ({ ...p, profileimage: URL.createObjectURL(file) }));
    uploadProfileImage(file);
  };

  const uploadProfileImage = (file) => {
    const userid = localStorage.getItem("user_id"); const fd = new FormData();
    fd.append("userid", userid); fd.append("profileimage", file);
    fetch(`${API_BASE_URL}api/updateProfileImage`, { method: "POST", body: fd })
      .then(r => r.json()).then(j => {
        if (j.status) {
          toast.success("Profile image updated");
          let u = {}; try { u = JSON.parse(localStorage.getItem("user") || "{}") || {}; } catch { u = {}; }
          u.profileimage = j.filename;
          localStorage.setItem("user", JSON.stringify(u));
          setUserData(p => ({ ...p, profileimage: j.filename }));
        }
      }).catch(e => console.error(e));
  };

  const handleLogout = () => {
    ["authToken","config_id","user_id","user","name","role"].forEach(k => localStorage.removeItem(k));
    toast.success("Logout Successfully"); setUserId(null);
    setTimeout(() => navigate("/index", { replace: true }), 100);
  };

  const handleChangeProfile = (e) => { const { name, value } = e.target; setUserData(p => ({ ...p, [name]: value })); };

  const handleProfileUpdate = (e) => {
    e.preventDefault(); const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/updateProfile`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userid, ...userData }) })
      .then(r => r.json()).then(j => { if (j.status) { toast.success("Profile updated"); localStorage.setItem("user", JSON.stringify(j.user || userData)); } })
      .catch(e => console.error(e));
  };

  const handleEdit = (address) => {
    setEditAddress(address);
    setAddressErrors({});
    setAddressForm({ house: address.house_flat || "", address: address.addressinfo || "", landmark: address.landmark || "", city: address.city || "", pincode: address.pincode || "", state: address.state || "", recipient: address.recipient || "", phone: address.mobile_number || "", orderFor: address.order_for || "self" });
    new window.bootstrap.Modal(document.getElementById("exampleModal")).show();
  };

  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Address",
      message: "Are you sure you want to delete this address? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      cancelLabel: "Cancel",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        const userid = localStorage.getItem("user_id");
        fetch(`${API_BASE_URL}api/deleteAddress`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userid, address_id: id }),
        })
          .then(r => r.json())
          .then(j => {
            if (j.status) {
              toast.success("Address deleted");
              if (j?.data) setAddresses(Object.values(j.data));
              else setAddresses(p => p.filter(a => a.id !== id));
            }
          })
          .catch(e => console.error(e));
      },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let cleaned = value;
    if (name === "phone") cleaned = value.replace(/\D/g, "").slice(0, 10);
    else if (name === "pincode") cleaned = value.replace(/\D/g, "").slice(0, 6);
    else if (name === "city" || name === "state") cleaned = value.replace(/[^a-zA-Z\s.'-]/g, "");
    else if (name === "recipient") cleaned = value.replace(/[^a-zA-Z\s.'-]/g, "").slice(0, 60);
    else if (name === "house") cleaned = value.slice(0, 50);
    else if (name === "address") cleaned = value.slice(0, 150);
    else if (name === "landmark") cleaned = value.slice(0, 80);
    setAddressForm(p => ({ ...p, [name]: cleaned }));
    if (addressErrors[name]) setAddressErrors(p => ({ ...p, [name]: "" }));
  };

  const handleAddressBlur = (e) => {
    const { name, value } = e.target;
    const err = validateAddressField(name, value, addressForm);
    setAddressErrors(p => ({ ...p, [name]: err }));
  };

  const handleAdd = () => { setEditAddress(null); setAddressForm(initialAddressForm); setAddressErrors({}); };

  const getCart = () => {
    fetch(`${API_BASE_URL}api/getCart`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: localStorage.getItem("device_id") }) })
      .then(r => r.json())
      .then(j => {
        setCartCount(j.count);
        const items = Object.values(j.cart || {});
        setCartAmount(items.reduce((s, i) => s + i.price * i.qty, 0));
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count: j.count } }));
      })
      .catch(e => console.error(e));
  };

  const getAddress = () => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/getAddress?userid=${userid}`)
      .then(r => r.json()).then(j => { if (j?.data) setAddresses(Object.values(j.data)); })
      .catch(e => console.error(e));
  };

  const getWishlist = () => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/myWishlistData?userid=${userid}`)
      .then(r => r.json()).then(j => { if (j?.data) { setWishlist(Object.values(j.data)); setWishlistCount(j.data.length); } })
      .catch(e => console.error(e));
  };

  const ordersList = (page = 1, search = "") => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/ordersList?userid=${userid}&page=${page}&orderid=${search}`)
      .then(r => r.json()).then(j => { if (j?.data) { setOrders(Object.values(j.data.data)); setOrdersCount(j.data.total); setCurrentPage(j.data.current_page); setTotalPages(Math.ceil(j.data.total / 10)); } })
      .catch(e => console.error(e));
  };

  const trackList = (page = 1) => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/trackuserorder?userid=${userid}&page=${page}`)
      .then(r => r.json()).then(j => { if (j?.data) setTrackList(Object.values(j.data.data)); })
      .catch(e => console.error(e));
  };

  const appointmentList = (page = 1) => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/userAppointments?userid=${userid}&page=${page}`)
      .then(r => r.json()).then(j => { if (j?.data) setAppointments(Object.values(j.data)); })
      .catch(e => console.error(e));
  };

  const addToCart = (id, qty) => {
    fetch(`${API_BASE_URL}api/addToCart`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ device_id: deviceId, id, qty }) })
      .then(r => r.json())
      .then(j => {
        const count = Object.keys(j.cart || {}).length;
        setCartCount(count);
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch(e => console.error(e));
  };

  const saveAddress = (e) => {
    e.preventDefault();
    const errors = validateAddress(addressForm);
    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors);
      toast.error("Please fix the highlighted fields");
      const firstKey = Object.keys(errors)[0];
      setTimeout(() => {
        const el = document.querySelector(`#exampleModal [name="${firstKey}"]`);
        if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      }, 50);
      return;
    }
    setAddressErrors({});
    const userid = localStorage.getItem("user_id"); const isEdit = !!editAddress?.id;
    const url = isEdit ? `${API_BASE_URL}api/updateAddress` : `${API_BASE_URL}api/saveAddress`;
    const payload = { userid, ...addressForm, ...(isEdit && { address_id: editAddress.id }) };
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      .then(r => r.json()).then(j => {
        if (j.status) {
          toast.success(isEdit ? "Address updated" : "Address saved");
          if (j?.data) setAddresses(Object.values(j.data));
          setEditAddress(null); setAddressForm(initialAddressForm);
          forceCloseModal("exampleModal");
        } else { toast.error(j.message || "Could not save address"); }
      }).catch(e => { console.error(e); toast.error("Network error. Please try again."); });
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    const errors = validatePassword(passwordData);
    if (Object.keys(errors).length > 0) { setPasswordErrors(errors); return; }
    setPasswordErrors({});
    const { current_password, new_password } = passwordData;
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/changePassword`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userid, current_password, new_password }) })
      .then(r => r.json()).then(j => { if (j.status) { toast.success("Password updated"); setPasswordData({ current_password: "", new_password: "", confirm_password: "" }); } else { toast.error(j.message || "Current password is incorrect"); } })
      .catch(e => console.error(e));
  };

  const handleWishlist = async (e, id) => {
    e.preventDefault();
    const userid = localStorage.getItem("user_id");
    try {
      const response = await fetch(`${API_BASE_URL}api/wishlist/add`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ product_id: id, userid }) });
      if (response.status === 201) toast.success("Added to wishlist");
      else if (response.status === 409) toast.success("Wishlist Deleted");
      getWishlist();
    } catch (error) { console.error("Wishlist error:", error); }
  };

  const getCurrentStep = (order) => {
    if (!order?.track_orders?.length) return -1;
    const lastStatus = order.track_orders[order.track_orders.length - 1]?.trackstatus;
    return STATUS_STEP_MAP[lastStatus] ?? -1;
  };

  /* ═══════ TWILIO HANDLERS ═══════ */
  const toggleMic = () => {
    if (!room) return;
    room.localParticipant.audioTracks.forEach(pub => { const t = pub.track; if (t) { isMuted ? t.enable() : t.disable(); } });
    setIsMuted(!isMuted);
  };

  const toggleCamera = () => {
    if (!room) return;
    room.localParticipant.videoTracks.forEach(pub => { const t = pub.track; if (t) { isCameraOff ? t.enable() : t.disable(); } });
    setIsCameraOff(!isCameraOff);
  };

  const leaveRoom = () => {
    if (room) {
      room.localParticipant.tracks.forEach(pub => { if (pub.track) { try { pub.track.stop?.(); pub.track.detach?.().forEach(el => el.remove()); } catch {} } });
      try { room.disconnect(); } catch {}
      const lv = document.getElementById("local-video"); if (lv) lv.innerHTML = "";
      const rv = document.getElementById("remote-video"); if (rv) rv.innerHTML = "";
      setRoom(null); roomRef.current = null; dataTrackRef.current = null;
    }
    forceCloseModal("videoModal");
  };

  const handleIncomingDataMessage = useCallback((raw) => {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.type === "chat" && parsed?.payload) {
        const incoming = { id: parsed.payload.id || `${Date.now()}_in`, sender: "doctor", text: sanitizeChatText(parsed.payload.text || ""), time: parsed.payload.time || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
        setChatMessages(p => [...p, incoming]);
        setChatUnread(u => (isChatOpen ? u : u + 1));
      }
    } catch (e) {}
  }, [isChatOpen]);

  const joinRoom = async (username, roomName, doctorDisplayName, apptType, apptId) => {
    if (!roomName) {
      showConfirm({ title: "Room Not Available", message: "This video call room is not available right now. Please try again later or contact support.", confirmLabel: "OK", confirmVariant: "warning", hideCancel: true, onConfirm: closeConfirm });
      return;
    }
    if (doctorDisplayName) setActiveDoctor(doctorDisplayName);
    setActiveApptType(apptType || null);
    setActiveApptId(apptId || roomName);
    new window.bootstrap.Modal(document.getElementById("videoModal")).show();
    try {
      const res = await fetch(`${API_BASE_URL}api/video/token`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, room: roomName }) });
      const data = await res.json();
      const localAV = await Video.createLocalTracks({ audio: true, video: { facingMode: "user" } });
      const localData = new Video.LocalDataTrack();
      dataTrackRef.current = localData;
      const roomObj = await Video.connect(data.token, { name: roomName, tracks: [...localAV, localData] });
      setRoom(roomObj); roomRef.current = roomObj;
      const lc = document.getElementById("local-video");
      if (lc) {
        lc.innerHTML = "";
        roomObj.localParticipant.videoTracks.forEach(pub => { if (pub.track) { const el = pub.track.attach(); el.style.cssText = "width:100%;height:100%;object-fit:cover;"; lc.appendChild(el); } });
      }
      const attach = (p) => {
        const c = document.getElementById("remote-video"); if (!c) return;
        const d = document.createElement("div");
        d.id = p.sid;
        d.style.cssText = "width:100%;height:100%;display:flex;align-items:center;justify-content:center;overflow:hidden;";
        c.appendChild(d);
        const attachTrack = (track) => {
          if (!track) return;
          if (track.kind === "data") { track.on("message", handleIncomingDataMessage); }
          else { const el = track.attach(); el.style.cssText = "width:100%;height:100%;object-fit:cover;"; d.appendChild(el); }
        };
        p.tracks.forEach(pub => { if (pub.isSubscribed && pub.track) attachTrack(pub.track); });
        p.on("trackSubscribed", attachTrack);
        p.on("trackUnsubscribed", (t) => { try { if (t.kind !== "data") t.detach().forEach(el => el.remove()); } catch {} });
        p.on("disconnected", () => d.remove());
      };
      roomObj.participants.forEach(attach);
      roomObj.on("participantConnected", attach);
      roomObj.on("participantDisconnected", p => { const el = document.getElementById(p.sid); if (el) el.remove(); });
    } catch (err) {
      console.error("Error joining room:", err);
      toast.error("Could not start the video call. Check camera/mic permissions.");
    }
  };

  useEffect(() => {
    if (!room) { setConnectionStatus("connecting"); return; }
    setConnectionStatus("live");
    const onRec = () => setConnectionStatus("reconnecting");
    const onReconnected = () => setConnectionStatus("live");
    const onDisc = () => setConnectionStatus("connecting");
    room.on("reconnecting", onRec); room.on("reconnected", onReconnected); room.on("disconnected", onDisc);
    return () => { try { room.off("reconnecting", onRec); room.off("reconnected", onReconnected); room.off("disconnected", onDisc); } catch {} };
  }, [room]);

  useEffect(() => {
    if (!room) { setCallSeconds(0); return; }
    const t = setInterval(() => setCallSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [room]);

  useEffect(() => {
    if (!room) { setIsSwapped(false); setIsChatOpen(false); setChatMessages([]); setChatInput(""); setChatUnread(0); setPipPos({ x: 0, y: 0 }); setFacingMode("user"); setActiveApptType(null); setActiveApptId(null); }
  }, [room]);

  useEffect(() => { if (isChatOpen) setChatUnread(0); }, [isChatOpen]);

  useEffect(() => {
    const onResize = () => setIsMobileVc(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Reviews section is admin-controlled and OFF by default; ask the backend.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
        const data = await res.json();
        if (data?.status) {
          setReviewsEnabled(!!data.data?.reviews_enabled);
          setDoctorReviewsEnabled(!!data.data?.doctor_reviews_enabled);
        }
      } catch (err) {
        console.log("reviews_enabled fetch error:", err);
      }
    })();
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (contentRef.current) contentRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeTab]);

  const formatDuration = (sec) => {
    const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); const s = sec % 60;
    const pad = n => n.toString().padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  const sendChatMessage = () => {
    const text = chatInput.trim(); if (!text) return;
    const safe = sanitizeChatText(text);
    const now = new Date();
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const msg = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, sender: "patient", text: safe, time };
    setChatMessages(prev => [...prev, msg]); setChatInput("");
    try { if (dataTrackRef.current) dataTrackRef.current.send(JSON.stringify({ type: "chat", payload: msg })); } catch (e) {}
  };

  const switchCamera = async () => {
    if (!room) return;
    try {
      const pub = Array.from(room.localParticipant.videoTracks.values())[0];
      const track = pub?.track; if (!track) { toast.info("Camera not ready"); return; }
      const next = facingMode === "user" ? "environment" : "user";
      if (typeof track.restart === "function") { await track.restart({ facingMode: next }); }
      else {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next }, audio: false });
        const newMst = stream.getVideoTracks()[0];
        if (typeof track.mediaStreamTrack?.stop === "function") track.mediaStreamTrack.stop();
        if (typeof track._setMediaStreamTrack === "function") track._setMediaStreamTrack(newMst);
      }
      setFacingMode(next);
      toast.success(`Switched to ${next === "user" ? "front" : "back"} camera`);
    } catch (err) { console.error("Camera switch error:", err); toast.error("Camera switch not supported on this device"); }
  };

  const requestPip = async () => {
    try {
      const container = document.getElementById("remote-video");
      const videoEl = container?.querySelector("video");
      if (!videoEl) { toast.info("No video yet to enter PiP"); return; }
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (typeof videoEl.requestPictureInPicture === "function") await videoEl.requestPictureInPicture();
      else toast.info("PiP not supported on this browser");
    } catch (err) { console.error("PiP error:", err); }
  };

  const onPipPointerDown = (e) => {
    const t = e.touches ? e.touches[0] : e;
    dragStateRef.current = { dragging: true, sx: t.clientX, sy: t.clientY, ox: pipPos.x, oy: pipPos.y };
    const onMove = (ev) => {
      if (!dragStateRef.current.dragging) return;
      const p = ev.touches ? ev.touches[0] : ev;
      const dx = p.clientX - dragStateRef.current.sx; const dy = p.clientY - dragStateRef.current.sy;
      const maxX = Math.max(0, window.innerWidth - 280); const maxY = Math.max(0, window.innerHeight - 280);
      setPipPos({ x: Math.max(-maxX, Math.min(0, dragStateRef.current.ox + dx)), y: Math.max(-maxY, Math.min(0, dragStateRef.current.oy + dy)) });
    };
    const onUp = () => { dragStateRef.current.dragging = false; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true }); window.addEventListener("touchend", onUp);
  };

  useEffect(() => { getCart(); getAddress(); getWishlist(); ordersList(); trackList(); appointmentList(); }, []);

  const filteredAddresses = addresses.filter(a => !addrSearch || [a.recipient, a.addressinfo, a.city, a.state, a.pincode].join(" ").toLowerCase().includes(addrSearch.toLowerCase()));
  const filteredWishlist = wishlist.filter(p => !wishSearch || p?.name?.toLowerCase().includes(wishSearch.toLowerCase()));
  const filteredAppts = appointments.filter(a => !apptSearch || [a.doctor?.name, a.doctor?.departmentdetails?.name, a.booking_date].join(" ").toLowerCase().includes(apptSearch.toLowerCase()));
  const filteredTracks = tracks.filter(o => !trackSearch || o.orderid?.toString().includes(trackSearch));
  const filteredOrders = orders.filter(o => !searchOrderId || o.orderid?.toString().includes(searchOrderId));

  /* ═══════════ RENDER SECTIONS ═══════════ */
  const renderAppointments = () => (
    <>
      <div className="mp-card">
        <div className="mp-card-head">
          <div className="mp-card-icon"><i className="fa-solid fa-calendar-check"></i></div>
          <div><h3 className="mp-card-title">Appointments</h3><p className="mp-card-sub">{appointments.length} appointment{appointments.length !== 1 ? "s" : ""}</p></div>
        </div>
        <div className="mp-search-bar">
          <i className="fa-solid fa-search"></i>
          <input type="text" placeholder="Search by doctor or department…" value={apptSearch} onChange={e => setApptSearch(e.target.value)} />
          {apptSearch && <button className="mp-search-clear" onClick={() => setApptSearch("")}><i className="fa-solid fa-xmark"></i></button>}
        </div>
        {filteredAppts.length > 0 ? (
          <div className="mp-appt-list">
            {filteredAppts.map((appt) => {
              const apptType   = getApptType(appt);
              const typeInfo   = getAppointmentTypeLabel(apptType);
              const isOnline   = apptType === APPT_TYPE.ONLINE;
              const isInPerson = apptType === APPT_TYPE.IN_PATIENT;
              const isUnknown  = apptType === APPT_TYPE.UNKNOWN;
              const beforeTime = isBeforeAppointmentTime(appt.booking_date, appt.booking_time);
              const callOpen   = isVideoAllowed(appt, appt.timeslot || 30);
              return (
                <div className="mp-appt-row" key={appt.id}>
                  <div className="mp-appt-avatar">{appt.doctor?.name?.charAt(0) || "D"}</div>
                  <div className="mp-appt-info">
                    <p className="mp-appt-doc">
                      {appt.doctor?.name}
                      <span className={`appt-type-pill ${typeInfo.cls}`} title={typeInfo.label}>
                        <i className={typeInfo.icon}></i> {typeInfo.label}
                      </span>
                    </p>
                    <p className="mp-appt-dept">{appt.doctor?.departmentdetails?.name}</p>
                    <p className="mp-appt-time">
                      <i className="fa-regular fa-calendar"></i> {appt.booking_date} &nbsp;
                      <i className="fa-regular fa-clock"></i> {appt.booking_time}
                    </p>
                  </div>
                  <div className="mp-appt-right">
                    <span className={`mp-appt-badge ${appt.status === "Pending" ? "text-warning" : "text-success"}`}>
                      <i className="fa-solid fa-circle-check"></i> {appt.status}
                    </span>
                    {isUnknown && <span className="mp-appt-unavail">Type not specified</span>}
                    {isInPerson && (
                      <span className="mp-appt-inperson" title="In-person consultation">
                        <i className="fa-solid fa-hospital"></i> Visit hospital
                      </span>
                    )}
                    {isOnline && callOpen && (
                      <button className="mp-btn-join" onClick={() => joinRoom(userData?.name, appt.room_name, appt.doctor?.name, apptType, appt.id)}>
                        <i className="fa-solid fa-video"></i> Join Call
                      </button>
                    )}
                    {isOnline && !callOpen && beforeTime && (
                      <span className="mp-appt-unavail" title={`Starts at ${formatApptDateTime(appt.booking_date, appt.booking_time)}`}>
                        <i className="fa-regular fa-clock"></i> Starts at {appt.booking_time}
                      </span>
                    )}
                    {isOnline && !callOpen && !beforeTime && (
                      <span className="mp-appt-unavail">Window closed</span>
                    )}
                    {appt.health_assessment && (
                      <button className="mp-appt-badge btn btn-success p-2" onClick={() => openHbaForm(appt, "view")}>
                        <i className="fa-solid fa-file-pdf"></i> View Consultation Form
                      </button>
                    )}
                    {doctorReviewsEnabled && appt.status === "Completed" && appt.doctor?.id && (
                      hasReviewedDoctor(appt.doctor.id) ? (
                        <span className="mp-review-done-badge">
                          <i className="fa-solid fa-check"></i> Reviewed
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="mp-btn-review mp-btn-review--sm"
                          onClick={() => openDoctorReviewModal(appt.doctor.id, appt.doctor.name)}
                        >
                          <i className="fa-solid fa-star"></i> Write a Review
                        </button>
                      )
                    )}
                  </div>
                  {isInPerson && (
                    <div className="appt-inperson-card">
                      <div className="appt-inperson-icon"><i className="fa-solid fa-hospital-user"></i></div>
                      <div>
                        <p className="appt-inperson-title">In-person appointment</p>
                        {appt.status !== "Pending" ? (
                          <p className="appt-inperson-sub">
                            This is a hospital visit — please reach the clinic on{" "}
                            <strong>{formatApptDateTime(appt.booking_date, appt.booking_time)}</strong>.
                          </p>
                        ) : (
                          <p className="appt-inperson-sub text-danger">Payment failed. Please try again.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mp-empty"><i className="fa-regular fa-calendar-xmark"></i><p>{apptSearch ? "No appointments match your search." : "No appointments found."}</p></div>
        )}
      </div>
      {/* HealthAssessmentForm now uses the top-level component */}
      <HealthAssessmentForm open={hbaOpen} mode={hbaMode} appointment={hbaTarget} onClose={closeHbaForm} />
    </>
  );

  const renderEditProfile = () => (
    <div className="mp-content-stack">
      <div className="mp-card">
        <div className="mp-card-head">
          <div className="mp-card-icon"><i className="fa-solid fa-user"></i></div>
          <div><h3 className="mp-card-title">Personal Information</h3><p className="mp-card-sub">Update your name, email and phone</p></div>
        </div>
        <form onSubmit={handleProfileUpdate}>
          <div className="mp-form-grid">
            <div className="mp-field"><label>Full Name</label><input type="text" name="name" value={userData?.name || ""} onChange={handleChangeProfile} placeholder="Your full name" /></div>
            <div className="mp-field"><label>Email Address</label><input type="email" name="email" value={userData?.email || ""} onChange={handleChangeProfile} placeholder="you@email.com" /></div>
            <div className="mp-field"><label>Phone Number</label><input type="text" name="mobile" value={userData?.mobile || ""} onChange={handleChangeProfile} placeholder="+91 00000 00000" /></div>
          </div>
          <button type="submit" className="mp-btn-primary"><i className="fa-solid fa-floppy-disk"></i> Save Changes</button>
        </form>
      </div>

      <div className="mp-card">
        <div className="mp-card-head">
          <div className="mp-card-icon"><i className="fa-solid fa-lock"></i></div>
          <div><h3 className="mp-card-title">Change Password</h3><p className="mp-card-sub">Keep your account secure</p></div>
        </div>
        <form onSubmit={handleChangePassword} noValidate>
          <div className="mp-form-grid">
            <div className={`mp-field ${passwordErrors.current_password ? "mp-field--error" : ""}`}>
              <label>Current Password <span className="mp-required">*</span></label>
              <input type="password" name="current_password" value={passwordData.current_password} onChange={handlePasswordChange} placeholder="Current password" className={passwordErrors.current_password ? "mp-input-error" : ""} />
              <FieldError msg={passwordErrors.current_password} />
            </div>
            <div className={`mp-field ${passwordErrors.new_password ? "mp-field--error" : ""}`}>
              <label>New Password <span className="mp-required">*</span></label>
              <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} placeholder="Min. 8 characters" className={passwordErrors.new_password ? "mp-input-error" : ""} />
              {passwordData.new_password && (() => {
                const { checks, strength } = getPasswordStrength(passwordData.new_password);
                return (
                  <div className="pwd-strength-wrap">
                    <div className="pwd-bars">
                      {[0,1,2].map(i => (<div key={i} className="pwd-bar" style={{ background: i < strength.bars ? strength.color : "#e5e7eb" }} />))}
                      <span className="pwd-strength-label" style={{ color: strength.color }}>{strength.label}</span>
                    </div>
                    <div className="pwd-checks">
                      {[{ key: "length", label: "8+ characters" },{ key: "uppercase", label: "Uppercase letter" },{ key: "lowercase", label: "Lowercase letter" },{ key: "number", label: "Number" },{ key: "special", label: "Special character" }].map(({ key, label }) => (
                        <span key={key} className={`pwd-check ${checks[key] ? "pwd-check--ok" : ""}`}>
                          <i className={`fa-solid ${checks[key] ? "fa-circle-check" : "fa-circle"}`} />{label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <FieldError msg={passwordErrors.new_password} />
            </div>
            <div className={`mp-field ${passwordErrors.confirm_password ? "mp-field--error" : ""}`}>
              <label>Confirm Password <span className="mp-required">*</span></label>
              <input type="password" name="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordChange} placeholder="Confirm new password" className={passwordErrors.confirm_password ? "mp-input-error" : ""} />
              {passwordData.confirm_password && (
                <span className={`pwd-match-pill ${passwordData.new_password === passwordData.confirm_password ? "pwd-match--ok" : "pwd-match--no"}`}>
                  <i className={`fa-solid ${passwordData.new_password === passwordData.confirm_password ? "fa-check-circle" : "fa-times-circle"}`} />
                  {passwordData.new_password === passwordData.confirm_password ? "Passwords match" : "Passwords do not match"}
                </span>
              )}
              <FieldError msg={passwordErrors.confirm_password} />
            </div>
          </div>
          <button type="submit" className="mp-btn-primary"><i className="fa-solid fa-key"></i> Update Password</button>
        </form>
      </div>
    </div>
  );

  const renderAddress = () => (
    <div className="mp-card">
      <div className="mp-card-head">
        <div className="mp-card-icon"><i className="fa-solid fa-location-dot"></i></div>
        <div style={{ flex: 1 }}><h3 className="mp-card-title">Saved Addresses</h3><p className="mp-card-sub">{addresses.length} address{addresses.length !== 1 ? "es" : ""} saved</p></div>
        <button className="mp-btn-primary mp-btn-sm" data-bs-toggle="modal" data-bs-target="#exampleModal" onClick={handleAdd}>
          <i className="fa-solid fa-plus"></i> Add New
        </button>
      </div>
      <div className="mp-search-bar">
        <i className="fa-solid fa-search"></i>
        <input type="text" placeholder="Search by name, city, pincode…" value={addrSearch} onChange={e => setAddrSearch(e.target.value)} />
        {addrSearch && <button className="mp-search-clear" onClick={() => setAddrSearch("")}><i className="fa-solid fa-xmark"></i></button>}
      </div>
      {filteredAddresses.length > 0 ? (
        <div className="mp-addr-grid">
          {filteredAddresses.map(addr => (
            <div className="mp-addr-card" key={addr.id}>
              <div className="mp-addr-card-top">
                <span className="mp-addr-tag"><i className="fa-solid fa-house"></i> {addr.order_for || "Home"}</span>
                <div className="mp-addr-actions">
                  <button className="mp-icon-btn mp-icon-btn--edit" onClick={() => handleEdit(addr)} title="Edit"><i className="fa-solid fa-pen"></i></button>
                  <button className="mp-icon-btn mp-icon-btn--del" onClick={() => handleDelete(addr.id)} title="Delete"><i className="fa-solid fa-trash"></i></button>
                </div>
              </div>
              <p className="mp-addr-name">{addr.recipient}</p>
              <p className="mp-addr-line">{addr.addressinfo}{addr.landmark ? `, ${addr.landmark}` : ""}</p>
              <p className="mp-addr-line">{addr.city}, {addr.state} — {addr.pincode}</p>
              <p className="mp-addr-phone"><i className="fa-solid fa-phone"></i> {addr.address_mobile || addr.mobile_number}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="mp-empty"><i className="fa-solid fa-location-dot"></i><p>{addrSearch ? "No addresses match your search." : "No addresses saved yet."}</p></div>
      )}
    </div>
  );

  const renderWishlist = () => (
    <div className="mp-card">
      <div className="mp-card-head">
        <div className="mp-card-icon"><i className="fa-solid fa-heart"></i></div>
        <div><h3 className="mp-card-title">My Wishlist</h3><p className="mp-card-sub">{wishlistCount} item{wishlistCount !== 1 ? "s" : ""} saved</p></div>
      </div>
      <div className="mp-search-bar">
        <i className="fa-solid fa-search"></i>
        <input type="text" placeholder="Search products…" value={wishSearch} onChange={e => setWishSearch(e.target.value)} />
        {wishSearch && <button className="mp-search-clear" onClick={() => setWishSearch("")}><i className="fa-solid fa-xmark"></i></button>}
      </div>
      {filteredWishlist.length > 0 ? (
        <div className="pv-prod-grid">
          {filteredWishlist.map(product => (
            <div key={product?.id} className="pv-prod-card">
              <span className="pv-prod-tag"><i className="fa-solid fa-circle-check" /> {product.dprice ? `${product.dprice}% Off` : "Hot Sale"}</span>
              <div className="pv-prod-img">
                <Link to={`/product/${product?.slug}`}>
                  <img src={product?.images ? `${API_BASE_URL}public/productimages/${product.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`} alt={product?.name} />
                </Link>
                <button type="button" className="pv-prod-wish" aria-label="Remove from wishlist" onClick={e => handleWishlist(e, product.id)}>
                  <i className="fa-solid fa-heart" />
                </button>
              </div>
              <div className="pv-prod-body">
                <p className="pv-prod-cat">{product?.category_title?.name}</p>
                <Link to={`/product/${product?.slug}`}><h4 className="pv-prod-name">{product?.name}</h4></Link>
                <div className="pv-prod-rating"><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-regular fa-star" /> <span>(240)</span></div>
                <div className="pv-prod-price"><b>₹{product?.price}</b></div>
                <button
                  type="button"
                  className="pv-prod-cart"
                  onClick={(e) => {
                    flyToCart(e.currentTarget.closest(".pv-prod-card").querySelector(".pv-prod-img"));
                    addToCart(product?.id, 1);
                  }}
                >
                  <i className="fa-solid fa-cart-shopping" /> Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mp-empty"><i className="fa-solid fa-heart"></i><p>{wishSearch ? "No products match your search." : "Your wishlist is empty."}</p></div>
      )}
    </div>
  );

  const renderTrackOrder = () => (
    <div className="mp-content-stack">
      <div className="mp-search-bar">
        <i className="fa-solid fa-search"></i>
        <input type="text" placeholder="Search by Order ID…" value={trackSearch} onChange={e => setTrackSearch(e.target.value)} />
        {trackSearch && <button className="mp-search-clear" onClick={() => setTrackSearch("")}><i className="fa-solid fa-xmark"></i></button>}
      </div>
      {filteredTracks.length > 0 ? filteredTracks.map(order => (
        <div className="mp-card" key={order.orderid}>
          <div className="mp-card-head">
            <div className="mp-card-icon"><i className="fa-solid fa-truck-fast"></i></div>
            <div>
              <h3 className="mp-card-title">Order #{order.orderid}</h3>
              <p className="mp-card-sub">{order.track_orders?.[order.track_orders.length - 1]?.trackdescription || "No tracking info"}</p>
            </div>
          </div>
          <div className="mp-stepper-v2">
            {ORDER_STEPS.map((step, index) => {
              const cur = getCurrentStep(order); const isDone = cur >= index; const isActive = cur === index;
              const colors = ORDER_STEP_COLORS[index];
              return (
                <div key={step.label} className={`mp-step-v2${isDone ? " mp-step-v2--done" : ""}${isActive ? " mp-step-v2--active" : ""}`}>
                  {index < ORDER_STEPS.length - 1 && <div className="mp-step-v2-line" style={{ background: isDone ? colors.color : "#e5e7eb" }} />}
                  <div className="mp-step-v2-dot" style={{ background: isDone || isActive ? colors.activeBg : colors.bg, border: `2px solid ${isDone || isActive ? colors.activeBg : colors.border}`, boxShadow: isActive ? `0 0 0 4px ${colors.bg}` : "none" }}>
                    <i className={`fa-solid ${isDone && !isActive ? "fa-check" : step.icon}`} style={{ color: isDone || isActive ? "#fff" : colors.color, fontSize: 13 }} />
                  </div>
                  <div className="mp-step-v2-label-wrap">
                    <span className="mp-step-v2-pill" style={isDone || isActive ? { background: colors.bg, color: colors.color, border: `1px solid ${colors.border}` } : {}}>
                      <i className={`fa-solid ${step.icon}`} style={{ fontSize: 9, marginRight: 3 }} />{step.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )) : (
        <div className="mp-card mp-empty"><i className="fa-solid fa-truck-fast"></i><p>{trackSearch ? "No orders match your search." : "No orders to track."}</p></div>
      )}
    </div>
  );

  const isOrderDelivered = (order) =>
    order?.status === "Delivered" || order?.status === "Product Delivered" || getCurrentStep(order) >= 4;

  const reviewKey = (orderId, productId) => `${orderId}_${productId}`;

  const hasReviewed = (orderId, productId) => reviewedKeys.includes(reviewKey(orderId, productId));

  const openReviewModal = (order, productId, item) => {
    setReviewTarget({
      orderId: order.orderid,
      productId,
      productName: item.name,
      productImage: item.image,
    });
    setReviewRating(0);
    setReviewHoverRating(0);
    setReviewComment("");
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setReviewTarget(null);
    setReviewRating(0);
    setReviewHoverRating(0);
    setReviewComment("");
  };

  const submitReview = async () => {
    if (!reviewTarget) return;
    if (!reviewsEnabled) {
      toast.error("Reviews are currently disabled.");
      return;
    }
    if (reviewRating < 1) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/order-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          order_id: reviewTarget.orderId,
          product_id: reviewTarget.productId,
          rating: reviewRating,
          comment: reviewComment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === undefined || data.status)) {
        const key = reviewKey(reviewTarget.orderId, reviewTarget.productId);
        const updated = [...reviewedKeys, key];
        setReviewedKeys(updated);
        localStorage.setItem("reviewed_order_products", JSON.stringify(updated));
        toast.success("Thank you for your review!");
        closeReviewModal();
      } else {
        toast.error(data?.message || "Could not submit review. Please try again.");
      }
    } catch (err) {
      console.log("submitReview error:", err);
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  const hasReviewedDoctor = (doctorId) => reviewedDoctorIds.includes(String(doctorId));

  const openDoctorReviewModal = (doctorId, doctorName) => {
    setDoctorReviewTarget({ doctorId, doctorName });
    setDoctorReviewRating(0);
    setDoctorReviewHoverRating(0);
    setDoctorReviewComment("");
    setShowDoctorReviewModal(true);
  };

  const closeDoctorReviewModal = () => {
    setShowDoctorReviewModal(false);
    setDoctorReviewTarget(null);
    setDoctorReviewRating(0);
    setDoctorReviewHoverRating(0);
    setDoctorReviewComment("");
  };

  const submitDoctorReview = async () => {
    if (!doctorReviewTarget) return;
    if (!doctorReviewsEnabled) {
      toast.error("Reviews are currently disabled.");
      return;
    }
    if (doctorReviewRating < 1) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmittingDoctorReview(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/doctor-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          doctor_id: doctorReviewTarget.doctorId,
          rating: doctorReviewRating,
          comment: doctorReviewComment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === undefined || data.status)) {
        const updated = [...reviewedDoctorIds, String(doctorReviewTarget.doctorId)];
        setReviewedDoctorIds(updated);
        localStorage.setItem("reviewed_doctor_ids", JSON.stringify(updated));
        toast.success("Thank you for your review!");
        closeDoctorReviewModal();
      } else {
        toast.error(data?.message || "Could not submit review. Please try again.");
      }
    } catch (err) {
      console.log("submitDoctorReview error:", err);
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmittingDoctorReview(false);
    }
  };

  const renderOrderHistory = () => (
    <div className="mp-card">
      <div className="mp-card-head">
        <div className="mp-card-icon"><i className="fa-solid fa-bag-shopping"></i></div>
        <div style={{ flex: 1 }}><h3 className="mp-card-title">Order History & Track Your Order</h3><p className="mp-card-sub">{ordersCount} total order{ordersCount !== 1 ? "s" : ""}</p></div>
      </div>
      <div className="mp-search-bar">
        <i className="fa-solid fa-search"></i>
        <input type="text" placeholder="Search by Order ID…" value={searchOrderId}
          onChange={e => { const v = e.target.value; setSearchOrderId(v); ordersList(1, v); }} />
        {searchOrderId && <button className="mp-search-clear" onClick={() => { setSearchOrderId(""); ordersList(1, ""); }}><i className="fa-solid fa-xmark"></i></button>}
      </div>
      {filteredOrders.length > 0 ? (
        <div className="mp-order-list">
          {filteredOrders.map(order => (
            <div className="mp-order-row" key={order.orderid}>
              <div className="mp-order-left">
                <span className="mp-order-id">#{order.orderid}</span>
                <span className="mp-order-meta"><i className="fa-regular fa-calendar"></i> {new Date(order.orderdate).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" })}</span>
                <span className="mp-order-meta">{Object.keys(order.order_products || {}).length} item{Object.keys(order.order_products || {}).length !== 1 ? "s" : ""}</span>
              </div>
              <div className="mp-order-right">
                <span className="mp-order-amount">
                  {order.final_amount && Number(order.final_amount) !== Number(order.totalamount) ? (
                    <><del style={{ color: "#9aa8a2", fontSize: 12, marginRight: 4 }}>₹{order.totalamount}</del><span style={{ color: "#059669" }}>₹{Number(order.final_amount).toFixed(2)}</span></>
                  ) : `₹${order.totalamount}`}
                </span>
                <span className={`mp-status-badge ${getStatusBadgeClass(order.status)}`}>{order.status}</span>
                {(() => { const s = getCurrentStep(order); const step = ORDER_STEPS[s]; if (!step) return null; return <span className="mp-order-step"><i className="fa-solid fa-circle-dot"></i> {step.label}</span>; })()}
                {reviewsEnabled && isOrderDelivered(order) && (() => {
                  const entries = Object.entries(order.order_products || {});
                  const pending = entries.find(([pid]) => !hasReviewed(order.orderid, pid));
                  if (!pending) return null;
                  const [pid, item] = pending;
                  return (
                    <button
                      type="button"
                      className="mp-btn-review mp-btn-review--sm"
                      onClick={() => openReviewModal(order, pid, item)}
                    >
                      <i className="fa-solid fa-star"></i> Rate Order
                    </button>
                  );
                })()}
                <button className="mp-btn-view" onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}>
                  <i className="fa-regular fa-eye"></i> View
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mp-empty"><i className="fa-solid fa-bag-shopping"></i><p>No orders found.</p></div>
      )}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={ordersList} />
    </div>
  );

  const navItems = [
    { key: "appointments", icon: "fa-solid fa-calendar-check", label: "My Appointments", badge: 0 },
    { key: "order-history", icon: "fa-solid fa-bag-shopping", label: "Order History", badge: ordersCount },
    { key: "track-order", icon: "fa-solid fa-truck-fast", label: "Track Order", badge: 0 },
    { key: "wishlist", icon: "fa-solid fa-heart", label: "My Wishlist", badge: wishlistCount },
    { key: "address", icon: "fa-solid fa-location-dot", label: "My Address", badge: 0 },
    { key: "edit-profile", icon: "fa-solid fa-user", label: "Edit Profile", badge: 0 },
  ];

  const mobileItems = [
    { key: "appointments", icon: "fa-solid fa-calendar-check", label: "My Appointments", render: renderAppointments, badge: 0 },
    { key: "order-history", icon: "fa-solid fa-bag-shopping", label: "Order History", render: renderOrderHistory, badge: ordersCount },
    { key: "track-order", icon: "fa-solid fa-truck-fast", label: "Track Order", render: renderTrackOrder, badge: 0 },
    { key: "wishlist", icon: "fa-solid fa-heart", label: "My Wishlist", render: renderWishlist, badge: wishlistCount },
    { key: "address", icon: "fa-solid fa-location-dot", label: "My Address", render: renderAddress, badge: 0 },
    { key: "edit-profile", icon: "fa-solid fa-user", label: "Edit Profile", render: renderEditProfile, badge: 0 },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case "appointments": return renderAppointments();
      case "order-history": return renderOrderHistory();
      case "edit-profile": return renderEditProfile();
      case "address": return renderAddress();
      case "wishlist": return renderWishlist();
      case "track-order": return renderTrackOrder();
      default: return renderEditProfile();
    }
  };

  const handleSaveOrder = () => {
    if (!selectedOrder) return;
    const orderData = {
      orderId: selectedOrder.orderid, orderDate: selectedOrder.orderdate,
      totalAmount: selectedOrder.totalamount, status: selectedOrder.status,
      paymentMethod: selectedOrder.payment_method || "Not specified",
      products: Object.entries(selectedOrder.order_products || {}).map(([id, item]) => ({
        productId: id, name: item.name, quantity: item.qty, price: item.price,
        discount: item.dprice || 0,
        finalPrice: item.dprice ? item.price - (item.price * item.dprice / 100) : item.price,
        image: item.image
      })),
      address: { fullAddress: selectedOrder.address, city: selectedOrder.city, state: selectedOrder.state, zipcode: selectedOrder.zipcode },
      delivery: selectedOrder.delivery || "Free", discount: selectedOrder.discount || 0,
      tracking: selectedOrder.track_orders || []
    };
    const blob = new Blob([JSON.stringify(orderData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Order_${selectedOrder.orderid}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success("Order details saved successfully!");
  };

  const handlePrintOrder = () => {
  if (!selectedOrder) return;

  const productsHtml = Object.entries(selectedOrder.order_products || {}).map(([id, item]) => {
    const finalPrice = item.dprice
      ? (item.price - (item.price * item.dprice / 100)).toFixed(2)
      : Number(item.price).toFixed(2);
    const lineTotal = (finalPrice * item.qty).toFixed(2);
    return `
      <tr>
        <td>${item.name}</td>
        <td style="text-align:center">${item.qty}</td>
        <td style="text-align:right">₹${Number(item.price).toFixed(2)}</td>
        <td style="text-align:center">${item.dprice ? item.dprice + '%' : '—'}</td>
        <td style="text-align:right">₹${finalPrice}</td>
        <td style="text-align:right">₹${lineTotal}</td>
      </tr>
    `;
  }).join('');

  const trackingHtml = (selectedOrder.track_orders || []).map(t => `
    <tr>
      <td>${t.trackstatus || '—'}</td>
      <td>${t.trackdescription || '—'}</td>
      <td>${t.created_at ? new Date(t.created_at).toLocaleString("en-IN") : '—'}</td>
    </tr>
  `).join('');

  const shippingAmount = Number(selectedOrder?.shipping_amount || 0);
  const couponAmount   = Number(selectedOrder?.coupen_amount  || 0);
  const totalMrp       = Number(selectedOrder?.totalamount    || 0);
  const amountPaid     = selectedOrder?.final_amount
    ? Number(selectedOrder.final_amount)
    : totalMrp;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) { toast.error("Please allow pop-ups to print order details"); return; }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Order #${selectedOrder.orderid}</title>
      <meta charset="UTF-8" />
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          color: #1a1a1a;
          background: #fff;
          padding: 32px;
          font-size: 13px;
        }

        /* ── Header ── */
        .pdf-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 3px solid #1E7A8C;
          padding-bottom: 20px;
          margin-bottom: 24px;
        }
        .pdf-brand { font-size: 22px; font-weight: 800; color: #1E7A8C; letter-spacing: -0.5px; }
        .pdf-brand-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
        .pdf-order-box { text-align: right; }
        .pdf-order-id { font-size: 20px; font-weight: 700; color: #111; }
        .pdf-order-date { font-size: 11px; color: #6b7280; margin-top: 3px; }
        .pdf-status {
          display: inline-block;
          margin-top: 6px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #6ee7b7;
        }

        /* ── Section title ── */
        .section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #1E7A8C;
          margin-bottom: 10px;
          padding-bottom: 5px;
          border-bottom: 1px solid #e5e7eb;
        }

        /* ── Info grid ── */
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }
        .info-box {
          background: #f8fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 14px 16px;
        }
        .info-box p { margin: 3px 0; font-size: 12.5px; color: #374151; line-height: 1.6; }
        .info-box strong { color: #111; }

        /* ── Products table ── */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 24px;
        }
        thead tr { background: #1E7A8C; }
        thead th {
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 10px 12px;
          text-align: left;
        }
        thead th:not(:first-child) { text-align: center; }
        thead th:last-child, thead th:nth-child(5), thead th:nth-child(6) { text-align: right; }
        tbody tr:nth-child(even) { background: #f9fafb; }
        tbody tr { border-bottom: 1px solid #e5e7eb; }
        tbody td { padding: 10px 12px; font-size: 12.5px; color: #374151; vertical-align: middle; }
        tbody td:first-child { font-weight: 600; color: #111; }

        /* ── Bill summary ── */
        .bill-wrap {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 24px;
        }
        .bill-table {
          width: 320px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        .bill-row {
          display: flex;
          justify-content: space-between;
          padding: 9px 14px;
          font-size: 12.5px;
          border-bottom: 1px solid #f3f4f6;
          color: #374151;
        }
        .bill-row:last-child { border-bottom: none; }
        .bill-row.total {
          background: #1E7A8C;
          color: #fff;
          font-weight: 700;
          font-size: 13.5px;
        }
        .bill-row.discount span:last-child { color: #16a34a; font-weight: 600; }
        .bill-row.free span:last-child { color: #16a34a; font-weight: 700; }

        /* ── Tracking table ── */
        .track-table thead tr { background: #374151; }
        .track-table tbody td { font-size: 12px; }

        /* ── Footer ── */
        .pdf-footer {
          border-top: 1px solid #e5e7eb;
          padding-top: 14px;
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #9ca3af;
        }

        @media print {
          body { padding: 20px; }
          @page { margin: 10mm; size: A4; }
        }
      </style>
    </head>
    <body>

      <!-- Header -->
      <div class="pdf-header">
        <div>
          <div class="pdf-brand">Dr. Shreevarma's Wellness</div>
          <div class="pdf-brand-sub">ayush.shreevarma.org</div>
        </div>
        <div class="pdf-order-box">
          <div class="pdf-order-id">Order #${selectedOrder.orderid}</div>
          <div class="pdf-order-date">
            ${new Date(selectedOrder.orderdate).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "2-digit" })}
          </div>
          <span class="pdf-status">${selectedOrder.status}</span>
        </div>
      </div>

      <!-- Info Grid -->
      <div class="info-grid">
        <div class="info-box">
          <div class="section-title">Shipping Address</div>
          <p>${[selectedOrder?.address, selectedOrder?.city, selectedOrder?.state, selectedOrder?.zipcode ? `- ${selectedOrder.zipcode}` : null].filter(Boolean).join(', ')}</p>
        </div>
        <div class="info-box">
          <div class="section-title">Order Details</div>
          <p><strong>Payment:</strong> ${selectedOrder.payment_method || 'Online'}</p>
          <p><strong>Items:</strong> ${Object.keys(selectedOrder.order_products || {}).length}</p>
          <p><strong>Coupon:</strong> ${selectedOrder.coupon_code || '—'}</p>
        </div>
      </div>

      <!-- Products -->
      <div class="section-title">Products Ordered</div>
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th style="text-align:center">Qty</th>
            <th style="text-align:right">MRP</th>
            <th style="text-align:center">Discount</th>
            <th style="text-align:right">Unit Price</th>
            <th style="text-align:right">Total</th>
          </tr>
        </thead>
        <tbody>${productsHtml}</tbody>
      </table>

      <!-- Bill Summary -->
      <div class="bill-wrap">
        <div class="bill-table">
          <div class="bill-row">
            <span>Total MRP</span>
            <span>₹${totalMrp.toLocaleString("en-IN")}</span>
          </div>
          <div class="bill-row ${shippingAmount === 0 ? 'free' : ''}">
            <span>Delivery</span>
            <span>${shippingAmount === 0 ? 'FREE' : '₹' + shippingAmount.toFixed(2)}</span>
          </div>
          ${couponAmount > 0 ? `
          <div class="bill-row discount">
            <span>Coupon Discount ${selectedOrder.coupon_code ? '(' + selectedOrder.coupon_code + ')' : ''}</span>
            <span>- ₹${couponAmount.toFixed(2)}</span>
          </div>` : ''}
          <div class="bill-row total">
            <span>Amount Paid</span>
            <span>₹${amountPaid.toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>

      ${trackingHtml ? `
      <!-- Tracking -->
      <div class="section-title">Order Tracking</div>
      <table class="track-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Description</th>
            <th>Date & Time</th>
          </tr>
        </thead>
        <tbody>${trackingHtml}</tbody>
      </table>
      ` : ''}

      <!-- Footer -->
      <div class="pdf-footer">
        <span>Dr. Shreevarma's Wellness · ayush.shreevarma.org</span>
        <span>Generated on ${new Date().toLocaleString("en-IN")}</span>
      </div>

      <script>
        window.onload = function() {
          setTimeout(function() { window.print(); window.close(); }, 400);
        };
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
};

  return (
    <>
      <Header />
      <div className="mp-breadcrumb-bar">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
              <li className="breadcrumb-item active">My Profile</li>
            </ol>
          </nav>
        </div>
      </div>

      <section className="mp-page">
        <div className="container">

          {/* DESKTOP */}
          <div className="mp-desktop-layout">
            <div className="mp-sidebar">
              <div className="mp-profile-hero">
                <div className="mp-avatar-wrap">
                  <img src={userData?.profileimage ? `${API_BASE_URL}public/profileimage/${userData.profileimage}` : "/front/assets/default-user.png"} alt="Profile" />
                  <button className="mp-avatar-btn" onClick={() => document.getElementById("profileImageInput").click()}>
                    <i className="far fa-camera"></i>
                  </button>
                  <input type="file" id="profileImageInput" accept="image/*" className="d-none" onChange={handleProfileImageChange} />
                </div>
                <p className="mp-profile-name">{userData?.name}</p>
                <p className="mp-profile-email">{userData?.email}</p>
              </div>
              <div className="mp-nav">
                {navItems.map(item => (
                  <button key={item.key} className={`mp-nav-btn ${activeTab === item.key ? "is-active" : ""}`} onClick={() => setActiveTab(item.key)}>
                    <span className="mp-nav-icon"><i className={item.icon}></i></span>
                    {item.label}
                    {item.badge > 0 && <span className="mp-nav-badge">{item.badge}</span>}
                  </button>
                ))}
                <hr className="mp-nav-divider" />
                <button className="mp-nav-btn is-logout" onClick={handleLogout}>
                  <span className="mp-nav-icon"><i className="fa-solid fa-arrow-right-from-bracket"></i></span>
                  Logout
                </button>
              </div>
            </div>
            <div ref={contentRef}>{renderActiveTab()}</div>
          </div>

          {/* MOBILE */}
          <div className="mp-mobile-layout">
            <div className="mp-mob-profile-card">
              <div className="mp-mob-hero">
                <img className="mp-mob-avatar" src={userData?.profileimage ? `${API_BASE_URL}public/profileimage/${userData.profileimage}` : "/front/assets/default-user.png"} alt="Profile" />
                <p className="mp-mob-name">{userData?.name}</p>
                <p className="mp-mob-email">{userData?.email}</p>
              </div>
              <div className="mp-mob-profile-bottom">
                <button className="mp-mob-cam-btn" onClick={() => document.getElementById("profileImageInputMobile").click()}>
                  <i className="far fa-camera me-2"></i>Change Photo
                </button>
                <input type="file" id="profileImageInputMobile" accept="image/*" className="d-none" onChange={handleProfileImageChange} />
              </div>
            </div>
            <div className="mp-mob-list">
              {mobileItems.map(({ key, icon, label, render, badge }) => {
                const isOpen = mobileAccordion === key;
                return (
                  <div key={key} className={`mp-mob-item${isOpen ? " is-open" : ""}`}>
                    <button className={`mp-mob-trigger${isOpen ? " is-active" : ""}`} onClick={() => toggleMobileAccordion(key)}>
                      <span className="mp-mob-trigger-left">
                        <span className="mp-mob-trigger-icon"><i className={icon}></i></span>
                        {label}
                      </span>
                      <span className="mp-mob-trigger-right">
                        {badge > 0 && <span className="mp-mob-badge">{badge}</span>}
                        <i className="fa-solid fa-chevron-down mp-mob-chevron"></i>
                      </span>
                    </button>
                    <div className={`mp-mob-body${isOpen ? " is-open" : ""}`}>
                      <div className="mp-mob-body-inner">{isOpen && render()}</div>
                    </div>
                  </div>
                );
              })}
              <div className="mp-mob-logout">
                <button className="mp-mob-logout-btn" onClick={handleLogout}>
                  <span className="mp-mob-logout-icon"><i className="fa-solid fa-arrow-right-from-bracket"></i></span>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADDRESS MODAL */}
      <div className="modal fade" id="exampleModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editAddress ? "Edit Address" : "Add New Address"}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">House / Floor / Flat <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" className={`form-control ${addressErrors.house ? "mp-modal-input-error" : ""}`} name="house" value={addressForm.house} onChange={handleChange} onBlur={handleAddressBlur} placeholder="e.g. Flat 4B, 2nd Floor" maxLength={50} />
                  {addressErrors.house && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.house}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Street Address <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" className={`form-control ${addressErrors.address ? "mp-modal-input-error" : ""}`} name="address" value={addressForm.address} onChange={handleChange} onBlur={handleAddressBlur} placeholder="Street, area, locality" maxLength={150} />
                  {addressErrors.address && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.address}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Landmark</label>
                  <input type="text" className={`form-control ${addressErrors.landmark ? "mp-modal-input-error" : ""}`} name="landmark" value={addressForm.landmark} onChange={handleChange} onBlur={handleAddressBlur} placeholder="Nearby landmark (optional)" maxLength={80} />
                  {addressErrors.landmark && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.landmark}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label">City <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" className={`form-control ${addressErrors.city ? "mp-modal-input-error" : ""}`} name="city" value={addressForm.city} onChange={handleChange} onBlur={handleAddressBlur} placeholder="City" maxLength={50} />
                  {addressErrors.city && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.city}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label">Pincode <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" inputMode="numeric" pattern="\d*" className={`form-control ${addressErrors.pincode ? "mp-modal-input-error" : ""}`} name="pincode" value={addressForm.pincode} onChange={handleChange} onBlur={handleAddressBlur} placeholder="6-digit PIN" maxLength={6} />
                  {addressErrors.pincode && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.pincode}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">State <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" className={`form-control ${addressErrors.state ? "mp-modal-input-error" : ""}`} name="state" value={addressForm.state} onChange={handleChange} onBlur={handleAddressBlur} placeholder="State" maxLength={50} />
                  {addressErrors.state && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.state}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Ordering for <span style={{ color: "#ef4444" }}>*</span></label>
                  <div className="d-flex gap-4 mt-1">
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="orderFor" id="orderSelf" value="self" checked={addressForm.orderFor === "self"} onChange={handleChange} />
                      <label className="form-check-label" htmlFor="orderSelf">Myself</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="orderFor" id="orderOther" value="other" checked={addressForm.orderFor === "other"} onChange={handleChange} />
                      <label className="form-check-label" htmlFor="orderOther">Someone else</label>
                    </div>
                  </div>
                  {addressErrors.orderFor && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.orderFor}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Recipient Name <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="text" className={`form-control ${addressErrors.recipient ? "mp-modal-input-error" : ""}`} name="recipient" value={addressForm.recipient} onChange={handleChange} onBlur={handleAddressBlur} placeholder="Full name" maxLength={60} />
                  {addressErrors.recipient && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.recipient}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number <span style={{ color: "#ef4444" }}>*</span></label>
                  <input type="tel" inputMode="numeric" pattern="\d*" className={`form-control ${addressErrors.phone ? "mp-modal-input-error" : ""}`} name="phone" value={addressForm.phone} onChange={handleChange} onBlur={handleAddressBlur} placeholder="10-digit mobile number" maxLength={10} />
                  {addressErrors.phone && <div className="mp-modal-field-error"><i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} /> {addressErrors.phone}</div>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              <button type="button" className="dc-book-btn" onClick={saveAddress}>
                <span><i className="fa-solid fa-floppy-disk me-2"></i>{editAddress ? "Update Address" : "Save Address"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* VIDEO MODAL */}
      <div className="modal fade" id="videoModal" tabIndex="-1">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="vc-shell">
              <VcTopBar status={connectionStatus} duration={formatDuration(callSeconds)} doctorName={activeDoctor} apptType={activeApptType} onClose={leaveRoom} />
              <div className={`vc-stage ${isChatOpen ? "vc-stage-with-chat" : ""}`}>
                <div className={`vc-tile ${isSwapped ? "vc-tile-float" : "vc-tile-main"}`} style={isSwapped ? { transform: `translate(${pipPos.x}px, ${pipPos.y}px)` } : undefined} onMouseDown={isSwapped ? onPipPointerDown : undefined} onTouchStart={isSwapped ? onPipPointerDown : undefined}>
                  <div id="remote-video" style={{ width: "100%", height: "100%" }} />
                  <div className="vc-tile-label"><i className="fa-solid fa-user-doctor" style={{ fontSize: 10 }} />{activeDoctor}</div>
                  {!isSwapped && connectionStatus !== "live" && (
                    <div className="vc-skeleton">
                      <div className="vc-skeleton-avatar">{(activeDoctor || "D").charAt(0).toUpperCase()}</div>
                      <div className="vc-skeleton-ring" />
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,.75)" }}>{connectionStatus === "reconnecting" ? "Reconnecting…" : "Connecting to doctor…"}</div>
                    </div>
                  )}
                </div>
                <div className={`vc-tile ${isSwapped ? "vc-tile-main" : "vc-tile-float"}`} style={!isSwapped ? { transform: `translate(${pipPos.x}px, ${pipPos.y}px)` } : undefined} onMouseDown={!isSwapped ? onPipPointerDown : undefined} onTouchStart={!isSwapped ? onPipPointerDown : undefined}>
                  <div id="local-video" style={{ width: "100%", height: "100%" }} />
                  <div className="vc-tile-label"><i className="fa-solid fa-user" style={{ fontSize: 10 }} />You</div>
                </div>
                <VcChatPanel open={isChatOpen} isMobile={isMobileVc} messages={chatMessages} input={chatInput} setInput={setChatInput} onSend={sendChatMessage} onClose={() => setIsChatOpen(false)} peerName={activeDoctor} />
              </div>
              <VcControlsBar isMuted={isMuted} isCameraOff={isCameraOff} onToggleMic={toggleMic} onToggleCam={toggleCamera} onSwitchCam={switchCamera} onSwap={() => setIsSwapped(v => !v)} onChat={() => setIsChatOpen(v => !v)} onEnd={leaveRoom} chatUnread={chatUnread} isMobile={isMobileVc} disabled={connectionStatus !== "live"} />
            </div>
          </div>
        </div>
      </div>

      {/* ORDER DETAIL MODAL */}
      {showOrderModal && selectedOrder && (
        <div className="order-modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div className="order-modal-box" onClick={e => e.stopPropagation()}>
            <div className="order-modal-header">
              <div>
                <p className="order-modal-title">Order #{selectedOrder.orderid}</p>
                <p className="order-modal-sub">
                  {new Date(selectedOrder.orderdate).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "2-digit" })}
                  {" · "}{Object.keys(selectedOrder.order_products || {}).length} item{Object.keys(selectedOrder.order_products || {}).length !== 1 ? "s" : ""}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className={`mp-status-badge ${getStatusBadgeClass(selectedOrder.status)}`}>{selectedOrder.status}</span>
                <button className="order-modal-close" onClick={() => setShowOrderModal(false)}><i className="fa-solid fa-xmark"></i></button>
              </div>
            </div>
            <div className="order-modal-body">
              <p className="order-section-title">Order Tracking</p>
              <div className="mp-stepper mb-4">
                {ORDER_STEPS.map((step, index) => {
                  const cur = getCurrentStep(selectedOrder); const isDone = cur >= index; const isActive = cur === index;
                  return (
                    <div key={step.label} className={`mp-step${isDone ? " mp-step--done" : ""}${isActive ? " mp-step--active" : ""}`}>
                      <div className="mp-step-dot">{isDone ? <i className="fa-solid fa-check" style={{ fontSize: 11 }}></i> : <i className={step.icon} style={{ fontSize: 11 }}></i>}</div>
                      <span className="mp-step-label">{step.label}</span>
                    </div>
                  );
                })}
              </div>
              <p className="order-section-title">Products</p>
              {selectedOrder?.order_products && Object.entries(selectedOrder.order_products).map(([id, item]) => (
                <div className="order-product-row" key={id}>
                  <img src={item.image || `${API_BASE_URL}public/noimage.jpg`} alt={item.name} className="order-product-thumb" onError={e => { e.target.src = `${API_BASE_URL}public/noimage.jpg`; }} />
                  <div className="order-product-info">
                    <p className="order-product-name">{item.name}</p>
                    <p className="order-product-meta">Qty: {item.qty} · ID: #{id}</p>
                  </div>
                  <div className="order-product-price">
                    {item.dprice && <del className="text-danger me-1">₹{item.price} </del>}
                    <span>₹{item.dprice ? item.price - (item.price * item.dprice / 100) : item.price}</span>
                  </div>
                  {reviewsEnabled && isOrderDelivered(selectedOrder) && (
                    hasReviewed(selectedOrder.orderid, id) ? (
                      <span className="mp-review-done-badge">
                        <i className="fa-solid fa-check"></i> Reviewed
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="mp-btn-review"
                        onClick={() => openReviewModal(selectedOrder, id, item)}
                      >
                        <i className="fa-solid fa-star"></i> Write a Review
                      </button>
                    )
                  )}
                </div>
              ))}
              <div className="order-info-grid">
                <div className="order-info-card">
                  <p className="order-section-title">Shipping Address</p>
                  <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, margin: 0 }}>
                    <i className="fa-solid fa-location-dot" style={{ color: "#1E7A8C", marginRight: 6 }}></i>
                    {[selectedOrder?.address, selectedOrder?.state, selectedOrder?.city, selectedOrder?.zipcode ? `- ${selectedOrder.zipcode}` : null].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="order-info-card">
                  <p className="order-section-title">Bill Summary</p>
                  <div className="bill-row"><span>Total MRP</span><span>₹{Number(selectedOrder?.totalamount || 0).toLocaleString("en-IN")}</span></div>
                  <div className="bill-row">
                    <span>Delivery</span>
                    <span>{Number(selectedOrder?.shipping_amount) === 0 ? <span style={{ color: "#16a34a", fontWeight: 700 }}>FREE</span> : `₹${Number(selectedOrder?.shipping_amount || 0).toFixed(2)}`}</span>
                  </div>
                  {Number(selectedOrder?.coupen_amount) > 0 && (
                    <div className="bill-row">
                      <span>Coupon Discount{selectedOrder?.coupon_code && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: "#ecfdf5", color: "#047857", border: "1px solid #b7e8d2", borderRadius: 999, padding: "1px 7px" }}>{selectedOrder.coupon_code}</span>}</span>
                      <span style={{ color: "#16a34a", fontWeight: 600 }}>- ₹{Number(selectedOrder.coupen_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="bill-row bill-total">
                    <span>Amount Paid</span>
                    <span>₹{selectedOrder?.final_amount ? Number(selectedOrder.final_amount).toLocaleString("en-IN") : Number(selectedOrder?.totalamount || 0).toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-3 justify-content-end">
                <button className="btn btn-outline-secondary btn-sm px-3 py-2" onClick={handlePrintOrder}>
                  <i className="fa-solid fa-print me-2"></i>Save Pdf
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Write a Review popup */}
      {reviewsEnabled && showReviewModal && reviewTarget && (
        <div className="order-modal-overlay" onClick={closeReviewModal}>
          <div className="mp-review-modal-box" onClick={e => e.stopPropagation()}>
            <div className="mp-review-modal-header">
              <p className="order-modal-title" style={{ margin: 0 }}>Write a Review</p>
              <button className="order-modal-close" onClick={closeReviewModal}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <div className="mp-review-modal-body">
              <div className="mp-review-product">
                <img
                  src={reviewTarget.productImage || `${API_BASE_URL}public/noimage.jpg`}
                  alt={reviewTarget.productName}
                  className="mp-review-product-thumb"
                  onError={e => { e.target.src = `${API_BASE_URL}public/noimage.jpg`; }}
                />
                <div>
                  <p className="mp-review-product-name">{reviewTarget.productName}</p>
                  <p className="mp-review-product-order">Order #{reviewTarget.orderId}</p>
                </div>
              </div>

              <p className="mp-review-label">How would you rate this product?</p>
              <div className="mp-star-picker" role="radiogroup" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = n <= (reviewHoverRating || reviewRating);
                  return (
                    <button
                      type="button"
                      key={n}
                      className="mp-star-btn"
                      role="radio"
                      aria-checked={reviewRating === n}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      onMouseEnter={() => setReviewHoverRating(n)}
                      onMouseLeave={() => setReviewHoverRating(0)}
                      onClick={() => setReviewRating(n)}
                    >
                      <i className={filled ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                    </button>
                  );
                })}
                {reviewRating > 0 && (
                  <span className="mp-star-rating-label">
                    {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewRating]}
                  </span>
                )}
              </div>

              <p className="mp-review-label">Share your experience (optional)</p>
              <textarea
                className="mp-review-textarea"
                rows={4}
                placeholder="What did you like or dislike about this product?"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                maxLength={1000}
              />

              <div className="d-flex gap-2 mt-3 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 py-2"
                  onClick={closeReviewModal}
                  disabled={submittingReview}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mp-btn-review-submit"
                  onClick={submitReview}
                  disabled={submittingReview || reviewRating < 1}
                >
                  {submittingReview ? (
                    <><i className="fa-solid fa-spinner fa-spin me-2"></i>Submitting…</>
                  ) : (
                    <><i className="fa-solid fa-paper-plane me-2"></i>Submit Review</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Write a Doctor Review popup */}
      {doctorReviewsEnabled && showDoctorReviewModal && doctorReviewTarget && (
        <div className="order-modal-overlay" onClick={closeDoctorReviewModal}>
          <div className="mp-review-modal-box" onClick={e => e.stopPropagation()}>
            <div className="mp-review-modal-header">
              <p className="order-modal-title" style={{ margin: 0 }}>Write a Review</p>
              <button className="order-modal-close" onClick={closeDoctorReviewModal}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <div className="mp-review-modal-body">
              <div className="mp-review-product">
                <div>
                  <p className="mp-review-product-name">{doctorReviewTarget.doctorName}</p>
                </div>
              </div>

              <p className="mp-review-label">How would you rate this doctor?</p>
              <div className="mp-star-picker" role="radiogroup" aria-label="Doctor rating">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = n <= (doctorReviewHoverRating || doctorReviewRating);
                  return (
                    <button
                      type="button"
                      key={n}
                      className="mp-star-btn"
                      role="radio"
                      aria-checked={doctorReviewRating === n}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      onMouseEnter={() => setDoctorReviewHoverRating(n)}
                      onMouseLeave={() => setDoctorReviewHoverRating(0)}
                      onClick={() => setDoctorReviewRating(n)}
                    >
                      <i className={filled ? "fa-solid fa-star" : "fa-regular fa-star"}></i>
                    </button>
                  );
                })}
                {doctorReviewRating > 0 && (
                  <span className="mp-star-rating-label">
                    {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][doctorReviewRating]}
                  </span>
                )}
              </div>

              <p className="mp-review-label">Share your experience (optional)</p>
              <textarea
                className="mp-review-textarea"
                rows={4}
                placeholder="What did you like about your consultation?"
                value={doctorReviewComment}
                onChange={(e) => setDoctorReviewComment(e.target.value)}
                maxLength={1000}
              />

              <div className="d-flex gap-2 mt-3 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 py-2"
                  onClick={closeDoctorReviewModal}
                  disabled={submittingDoctorReview}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mp-btn-review-submit"
                  onClick={submitDoctorReview}
                  disabled={submittingDoctorReview || doctorReviewRating < 1}
                >
                  {submittingDoctorReview ? (
                    <><i className="fa-solid fa-spinner fa-spin me-2"></i>Submitting…</>
                  ) : (
                    <><i className="fa-solid fa-paper-plane me-2"></i>Submit Review</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        confirmVariant={confirmDialog.confirmVariant}
        hideCancel={confirmDialog.hideCancel}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />

      <MobileBottomNav userId={userId} />
      <Footer />
    </>
  );
};

export default Checkout;